/**
 * Page 2 — "The Lagna Navigator" heatmap landing page (the DEFAULT page for every loaded
 * profile). The single user journey is to traverse the heatmap and find the actual birth time,
 * per PLAN.md's process: rising sign first (Cycle 1 / Level 0), then the timed window (Cycle 2
 * / Level 1), then the exact second (drill-down). It is a presentation layer over the SAME
 * deterministic engine PLAN.md describes — it never invents a score.
 *
 * Visual language: cosmic-modern dark (deep-night palette, gold/starlight accents, Newsreader
 * serif display + IBM Plex Mono data). The heatmap bars read TWICE — by HEIGHT (exact score)
 * and by HUE band (grey unlikely · blue maybe · green likely).
 */

import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useProfile } from "../state/ProfileContext";
import { sign_rising_window } from "../lib/rising_sign_confidence";
import {
  sign_appearance_signature,
  all_sign_signatures,
  SIGN_NAMES,
  TRAIT_QUESTIONS,
  INFLUENCE_FACTORS,
  type InfluenceLevel,
} from "../lib/traits";
import {
  precision_tier,
  candidate_cluster,
  ANCHOR_EVENT_SUGGESTIONS,
} from "../lib/precision";
import { candidate_list_from_times, replace_search_window } from "../lib/json_store";
import { audit_candidate, audit_sentence } from "../lib/candidate_audit";
import {
  search_span_seconds,
  FINE_DIVISIONAL_MAX_SPAN_SECONDS,
} from "../lib/scoring";
import { EventsPanelBody } from "./EventManager";
import { TraitChatPanel, option_label } from "./TraitsPanel";
import {
  Card,
  Banner,
  Expander,
  Modal,
  LoadingLine,
  useAsyncAction,
} from "../ui/common";
import { fullClock, score as fmtScore, shortTime, titleCase } from "../ui/format";
import {
  bandLabel,
  heatmapBand,
  bandGradient,
  secondsOfLabel,
  dateOfLabel,
  labelOfSeconds,
  forwardWidthSeconds,
  subWindowDates,
  zoomOutTarget,
} from "../ui/heatmap";
import {
  addDays,
  diffDays,
  formatISODate,
  parseISODate,
  plainTime,
  tryParseISOTime,
  type PlainDate,
  type PlainTime,
} from "../lib/datetime";
import {
  add_marked_time,
  marked_time_key,
  normalize_marked_times,
  resolve_marked_date,
  MAX_MARKED_TIMES,
} from "../lib/time_compare";

/** Classical Sanskrit (Rashi) name per English sign — shown beneath each band/name. */
export const SANSKRIT: Record<string, string> = {
  Aries: "Mesha",
  Taurus: "Vrishabha",
  Gemini: "Mithuna",
  Cancer: "Karka",
  Leo: "Simha",
  Virgo: "Kanya",
  Libra: "Tula",
  Scorpio: "Vrishchika",
  Sagittarius: "Dhanu",
  Capricorn: "Makara",
  Aquarius: "Kumbha",
  Pisces: "Meena",
};

/**
 * True when a saved search window genuinely spans MORE than 24h (multi-day induced-delivery
 * search) — the same date-aware span math as the Level-0 strip. Used to guard the date
 * renormalisation below: a multi-day commit carries its own explicit day (the bestDay branch),
 * so its dates must never be rewritten through the single-day/overnight rule.
 */
function searchIsMultiday(sr: any): boolean {
  if (!sr?.start_date || !sr?.end_date) return false;
  const gap = diffDays(parseISODate(sr.end_date), parseISODate(sr.start_date));
  return (
    gap * 86400 +
      (secondsOfLabel(sr.end ?? "23:59:59") - secondsOfLabel(sr.start ?? "00:00:00")) >
    86400
  );
}

/**
 * After sr.start/sr.end were narrowed to a sub-window of the PREVIOUS window (base), renormalise
 * sr.start_date/sr.end_date via the shared subWindowDates rule — so committing/zooming into a
 * non-crossing band of an overnight window (21:00→01:00, dates D/D+1) never leaves a stale
 * end_date=D+1 (which would read as a ~25h multi-day window and pin the engine to coarse scans),
 * and a post-midnight band correctly lands on D+1/D+1. No-op when the profile carries no window
 * dates (nothing stale to fix) or when the base was multi-day (explicit-day commits only).
 */
function renormalizeWindowDates(
  sr: any,
  base: { multiday: boolean; start: string | null; start_date: string | null },
): void {
  if (base.multiday || !base.start || !base.start_date) return;
  if (sr.start_date == null && sr.end_date == null) return;
  const d = subWindowDates(base.start_date, base.start, sr.start, sr.end);
  sr.start_date = d.start_date;
  sr.end_date = d.end_date;
}

// ── Small cosmic building blocks ──────────────────────────────────────────────────────────────

export function Legend({ rank }: { rank?: boolean } = {}) {
  if (rank) {
    // Matches the value-GRADIENT strips: lowest red → middle blue → highest green.
    return (
      <div className="heatmap-legend" aria-hidden>
        <span className="sw" style={{ background: gradientBgForT(0) }} /> lowest
        <span className="sw" style={{ background: gradientBgForT(0.5) }} />{" "}
        middle
        <span className="sw" style={{ background: gradientBgForT(1) }} />{" "}
        highest
      </div>
    );
  }
  return (
    <div className="heatmap-legend" aria-hidden>
      <span className="sw" style={{ background: bandGradient(40) }} /> unlikely
      <span className="sw" style={{ background: bandGradient(62) }} /> maybe
      <span className="sw" style={{ background: bandGradient(85) }} /> likely
      <span className="sw" style={{ background: bandGradient(null) }} /> none
    </div>
  );
}

const TIER_ORDER = ["window", "minute", "second"] as const;
const TIER_LABEL: Record<string, string> = {
  window: "WINDOW",
  minute: "MINUTE",
  second: "SECOND",
};

/** The WINDOW → MINUTE → SECOND precision rail — the visual twin of PLAN's precision gate. */
function PrecisionTiers({ tier }: { tier: string }) {
  const cur = TIER_ORDER.indexOf(tier as (typeof TIER_ORDER)[number]);
  return (
    <div className="precision-tiers">
      {TIER_ORDER.map((t, i) => {
        const cls = i === cur ? "tier active" : i < cur ? "tier done" : "tier";
        return (
          <Fragment key={t}>
            <div className={cls}>
              <span className="dot" />
              <span className="label">{TIER_LABEL[t]}</span>
            </div>
            {i < 2 && <span className="tier-sep">→</span>}
          </Fragment>
        );
      })}
    </div>
  );
}

const _MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** A PlainDate → compact "7 Sep" label for the multi-day axis and band sub-labels. */
function shortDay(d: { year: number; month: number; day: number }): string {
  return `${d.day} ${_MONTHS[d.month - 1] ?? d.month}`;
}

/** Day boundary labels for the multi-day linear axis (replaces the 24h time ticks). */
function DayAxis({ markers }: { markers: { label: string; leftFrac: number }[] }) {
  return (
    <div className="heatmap-axis" style={{ position: "relative", height: 18 }}>
      {markers.map((m, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            left: `${(m.leftFrac * 100).toFixed(2)}%`,
            transform: i === 0 ? "none" : "translateX(-50%)",
            whiteSpace: "nowrap",
          }}
        >
          {m.label}
        </span>
      ))}
    </div>
  );
}

function AxisTicks({ start, end }: { start: string; end: string }) {
  const s = secondsOfLabel(start);
  const span = forwardWidthSeconds(start, end);
  const ticks: string[] = [];
  for (let i = 0; i < 5; i++) ticks.push(labelOfSeconds(s + (span * i) / 4));
  return (
    <div className="heatmap-axis">
      {ticks.map((t, i) => (
        <span key={i}>{shortTime(t)}</span>
      ))}
    </div>
  );
}

export interface Bar {
  key: string;
  /** Absolute placement on the time axis (fractions 0–1 of the strip) so bars line up with the ticks. */
  leftFrac: number;
  widthFrac: number;
  score: number | null;
  name: string;
  sub: string;
  pct: string;
  mono?: boolean;
  cursor?: string;
  disabled?: boolean;
  /** Optional hover tooltip override (e.g. the rising-sign time range) — shown instead of the band. */
  hint?: string;
  /** Per-metric values; the strip shows ONE of these at a time, chosen by the toggle above it. */
  metrics?: {
    events: number | null;
    eras: number | null;
    appearance: number | null;
    personality: number | null;
    /** Level-1 drill + Compare page: per-candidate leave-one-out stability (accuracy_fit). */
    accuracy?: number | null;
    /** Level-0 signs: weighted % of events explained (scored >50) at the sign's best moment. */
    explains?: number | null;
    derived: number | null;
    /** The comparison page's user promote/demote standing (raw signed level, not a %). */
    rank?: number | null;
  };
  onClick: () => void;
}

/** The selectable metrics for the toggle above the strip — only one bar is shown at a time. */
export type MetricKey =
  | "events"
  | "eras"
  | "personality"
  | "appearance"
  | "explains"
  // Level-1 drill + Compare page only: per-candidate leave-one-out stability (accuracy_fit).
  // NOT a Level-0 sign metric — the per-sign holdout Accuracy was replaced by "explains".
  | "accuracy"
  | "derived"
  | "rank";
const METRIC_OPTIONS = [
  {
    key: "events",
    label: "Events",
    help: "How well the dated events time out under each sign.",
  },
  {
    key: "eras",
    label: "Eras",
    help: "How well the answered life-periods (what each era was about, good or hard) time out under each sign — its own dasha-boundary signal, blended into Derived. Blank until you answer some eras.",
  },
  {
    key: "personality",
    label: "Traits",
    help: "Modern-astrology personality match (Moon sign) — WEAK evidence: prone to the Barnum effect, never decides the rising sign.",
  },
  {
    key: "appearance",
    label: "Appearance",
    help: "Physical resemblance to the rising sign.",
  },
  {
    key: "explains",
    label: "Explains",
    help: "How many of your dated events this sign actually EXPLAINS (scores above neutral) at its best moment — the classical acceptance rule made visible. Replaced the old per-sign holdout Accuracy, which measured re-rectification flexibility and ranked the true sign worse than chance on the reference charts.",
  },
  {
    key: "derived",
    label: "Derived",
    help: "The final lagna recommendation — a weighted blend of every signal present: Events (×1.0), Explains (×0.5), Eras (×0.3), Appearance (×0.2) and Personality (×0.2), normalised over whichever are available.",
  },
] as const satisfies ReadonlyArray<{
  key: MetricKey;
  label: string;
  help: string;
}>;
const METRIC_LABEL: Record<MetricKey, string> = Object.fromEntries(
  METRIC_OPTIONS.map((m) => [m.key, m.label]),
) as Record<MetricKey, string>;

/**
 * The drill-down (Level-1, exact-time) toggle shows ONLY the two signals that actually vary second
 * to second within one committed rising sign — appearance/personality/family are flat across the
 * window (they decided the SIGN on the previous page), so they would render as uninformative bars.
 *   • Events   — the influence-weighted KP fit that RANKS the exact time.
 *   • Explains — how many events score above neutral at THIS exact second (same definition as the
 *                Level-0 Explains column — one metric, every surface).
 *   • Accuracy — held-out corroboration (`accuracy_fit`): holding out each event in turn, how well it
 *                still independently vouches for THIS exact time. The page also shows the real
 *                re-rectified holdout verdict (re-search the window without each event) as context.
 */
type DrillMetricKey = "events" | "explains" | "accuracy";
const DRILL_METRIC_OPTIONS = [
  {
    key: "events",
    label: "Events",
    help: "How well the dated events time out at each candidate second (influence-weighted) — this RANKS the exact time.",
  },
  {
    key: "explains",
    label: "Explains",
    help: "How many of your dated events score above neutral at this exact second — the acceptance rule per candidate (same definition as the Level-0 Explains column).",
  },
  {
    key: "accuracy",
    label: "Accuracy",
    help: "Held-out corroboration: holding out each event in turn, how well it still independently supports this exact time. A time propped up by a single event reads lower here than under Events.",
  },
] as const satisfies ReadonlyArray<{
  key: DrillMetricKey;
  label: string;
  help: string;
}>;

/** Smallest visible fill height (%) — the lowest value in a metric still shows a sliver. */
const MIN_BAR_HEIGHT = 6;

/** Min/max of the non-null values in a metric, or null when nothing to scale against. */
function metricRange(
  vals: (number | null | undefined)[],
): { min: number; max: number } | null {
  const ns = vals.filter((v): v is number => v != null);
  if (!ns.length) return null;
  return { min: Math.min(...ns), max: Math.max(...ns) };
}

/**
 * Stretch a value to a bar HEIGHT (%): the metric's own min→max is mapped onto
 * MIN_BAR_HEIGHT→100, so small mid-range differences become visible. Colour is decided
 * separately from the ABSOLUTE score (bandGradient), so the stretch never recolours a bar.
 */
function stretchHeight(
  v: number | null | undefined,
  range: { min: number; max: number } | null,
): number {
  if (v == null) return MIN_BAR_HEIGHT; // no data → a small nub
  // Flat range (every bar the same value): render a consistent MID height — the height twin of
  // valueGradientBg's neutral fill for a flat range — instead of leaking the raw value as pixels.
  if (!range || range.max <= range.min) return MIN_BAR_HEIGHT + (100 - MIN_BAR_HEIGHT) / 2;
  const t = (v - range.min) / (range.max - range.min); // 0..1 within this metric
  return MIN_BAR_HEIGHT + t * (100 - MIN_BAR_HEIGHT);
}

const RANK_NEUTRAL = "var(--n1)";

// Value-GRADIENT bar fills (owner decision 2026-06-25, replacing the earlier rank colours): the bar
// hue is the value's position between the strip's MIN and MAX — lowest = red, middle = white, highest
// = green, interpolated smoothly. So two bars with the SAME score read the SAME colour (rank colours
// gave tied scores different hues), and the strip reads as a true low→high gradient of the values.
const GRAD_MIN: [number, number, number] = [206, 35, 35]; // red   (lowest value)
const GRAD_MID: [number, number, number] = [240, 240, 240]; // white (middle value)
const GRAD_MAX: [number, number, number] = [35, 190, 35]; // green (highest value)
function _mix(
  a: [number, number, number],
  b: [number, number, number],
  t: number,
): [number, number, number] {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ];
}
/** Fill for a normalised value t∈[0,1]: 0→red, 0.5→white, 1→green (a vertical gradient for depth). */
function gradientBgForT(t: number): string {
  t = Math.max(0, Math.min(1, t));
  const [r, g, b] =
    t <= 0.5
      ? _mix(GRAD_MIN, GRAD_MID, t / 0.5)
      : _mix(GRAD_MID, GRAD_MAX, (t - 0.5) / 0.5);
  const light = `rgb(${Math.min(255, r + 28)}, ${Math.min(255, g + 28)}, ${Math.min(255, b + 28)})`;
  return `linear-gradient(180deg, ${light}, rgb(${r}, ${g}, ${b}))`;
}
/** Fill for a bar value `v` given the strip's min/max — neutral when v is null or the range is flat. */
function valueGradientBg(
  v: number | null | undefined,
  range: { min: number; max: number } | null,
): string {
  if (v == null || !range || range.max <= range.min) return RANK_NEUTRAL;
  return gradientBgForT((v - range.min) / (range.max - range.min));
}

/**
 * The bar strip — one bar per range, rising from the bottom (height = stretched value). By default
 * the hue is the ABSOLUTE confidence band (`bandGradient`). With `rankColors` the hue is instead a
 * MIN→MAX value GRADIENT across the strip (lowest red · middle blue · highest green) — so equal
 * scores read equal and the strip shows the spread of the values. When `metric` is given the bar
 * shows that single chosen signal (selected by the toggle above the strip); otherwise it falls back
 * to the blended `score`.
 */
export function HeatmapBars({
  bars,
  label,
  metric,
  rankColors,
  formatValue,
}: {
  bars: Bar[];
  label: string;
  metric?: MetricKey;
  rankColors?: boolean;
  /** Value label override for non-% metrics (the compare page's signed rank levels). */
  formatValue?: (v: number | null | undefined, metric: MetricKey) => string;
}) {
  // INSTANT hover tooltip (no native `title` delay): a strip-level pill, centred over the hovered
  // bar, set immediately on mouseenter. Strip-level (not inside the cell) so a thin bar can't clip a
  // wider time-range label. Only bars that carry a `hint` (the Level-0 rising-sign strip) show it.
  const [tip, setTip] = useState<{ text: string; centerFrac: number } | null>(
    null,
  );
  // The shown value per bar — the selected metric when bars carry one, else the blended score.
  const valueOf = (b: Bar): number | null =>
    metric && b.metrics ? (b.metrics[metric] ?? null) : b.score;
  // Min/max-normalise across the strip so heights span the full range of whatever is shown.
  const range = metricRange(bars.map(valueOf));
  return (
    <div className="heatmap-strip" role="group" aria-label={label}>
      {bars.map((b) => {
        const v = valueOf(b);
        const band = heatmapBand(v);
        // The highest-valued bar(s) keep the green "glow" accent; ties at the max all glow (equal reads equal).
        const isTop = rankColors
          ? v != null && range != null && v >= range.max
          : band === "green";
        const fillBg = rankColors ? valueGradientBg(v, range) : bandGradient(v);
        const showTip = () => {
          if (b.hint)
            setTip({ text: b.hint, centerFrac: b.leftFrac + b.widthFrac / 2 });
        };
        const hideTip = () => {
          if (b.hint) setTip(null);
        };
        return (
          <button
            key={b.key}
            className={`heatmap-cell${isTop ? " green" : ""}`}
            style={{
              left: `${b.leftFrac * 100}%`,
              width: `${b.widthFrac * 100}%`,
              cursor: b.cursor,
            }}
            // No native `title` on hint-carrying bars — the instant pill below replaces its slow tooltip.
            title={b.hint ? undefined : `${b.name} — ${bandLabel(v)}`}
            disabled={b.disabled}
            onClick={b.onClick}
            onMouseEnter={showTip}
            onMouseLeave={hideTip}
            onFocus={showTip}
            onBlur={hideTip}
          >
            {/* Heading ABOVE the bar (the name), so it never sits on top of the fill. */}
            <div className="cell-head">
              <span
                className="cell-name"
                style={
                  b.mono
                    ? { fontFamily: "var(--mono)", fontSize: 11 }
                    : undefined
                }
              >
                {b.name}
              </span>
              {b.sub && <div className="cell-sub">{b.sub}</div>}
            </div>
            <div className="cell-plot">
              <span className="cell-pct">
                {metric && b.metrics
                  ? formatValue
                    ? formatValue(v, metric)
                    : fmtPct(v)
                  : b.pct}
              </span>
              <div
                className={`heatmap-fill${isTop ? " green" : ""}`}
                style={{
                  height: `${stretchHeight(v, range)}%`,
                  background: fillBg,
                }}
              />
            </div>
          </button>
        );
      })}
      {tip && (
        <div
          className="cell-tip-float"
          style={{ left: `${tip.centerFrac * 100}%` }}
          aria-hidden
        >
          {tip.text}
        </div>
      )}
    </div>
  );
}

/**
 * The two headline fits shown directly under the panel title — the best Events fit and best
 * Traits fit across the strip, each tagged with the sign that leads it. Always visible (not
 * hover-gated) so the strongest evidence in each channel is readable at a glance.
 */
function TopFits({
  chips,
}: {
  chips: Array<{ label: string; value: number | null; sign?: string }>;
}) {
  const fmt = (v: number | null) => (v != null ? `${v.toFixed(1)}%` : "—");
  return (
    <div className="top-fits">
      {chips.map((c) => (
        <span className="top-fit-chip" key={c.label}>
          <span className="top-fit-k">{c.label}</span>
          <span className="top-fit-v">{fmt(c.value)}</span>
          {c.sign && c.value != null && (
            <span className="top-fit-sign">{c.sign}</span>
          )}
        </span>
      ))}
    </div>
  );
}

/** The precision hero strip beneath the bars: tier rail + best readout + resolution box. */
function PrecisionHero({
  tier,
  readout,
  range,
  message,
  resolutionLabel,
}: {
  tier: string;
  readout: string;
  range: string;
  message: string;
  resolutionLabel: string;
}) {
  const boxColor =
    tier === "second"
      ? "var(--g2)"
      : tier === "minute"
        ? "var(--accent2)"
        : "var(--ink2)";
  return (
    <div
      style={{
        marginTop: 15,
        borderTop: "1px solid var(--line)",
        paddingTop: 14,
        display: "flex",
        alignItems: "center",
        gap: 20,
        flexWrap: "wrap",
      }}
    >
      <PrecisionTiers tier={tier} />
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <span className="estimate">{readout}</span>
          <span style={{ fontSize: 12, color: "var(--ink2)" }}>{range}</span>
        </div>
        <div style={{ fontSize: 12, color: "var(--ink2)", marginTop: 3 }}>
          {message}
        </div>
      </div>
      <div
        style={{
          textAlign: "right",
          padding: "9px 15px",
          borderRadius: 11,
          border: "1px solid var(--line)",
          background: "var(--bg3)",
          color: boxColor,
        }}
      >
        <div
          style={{
            fontSize: 9.5,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            opacity: 0.75,
          }}
        >
          Resolution claimed
        </div>
        <div
          style={{
            fontFamily: "var(--serif)",
            fontSize: 18,
            fontWeight: 600,
            textTransform: "capitalize",
          }}
        >
          {resolutionLabel}
        </div>
      </div>
    </div>
  );
}

/** Numbered, serif-titled panel header for the collapsible Expanders. */
export function PanelSummary({
  no,
  title,
  meta,
}: {
  no: string;
  title: string;
  meta?: string;
}) {
  return (
    <>
      <span className="tree-score" style={{ fontSize: 11 }}>
        {no}
      </span>
      <span className="panel-title">{title}</span>
      {meta && <span className="caption">{meta}</span>}
    </>
  );
}

// ── The page ──────────────────────────────────────────────────────────────────────────────────

export function HeatmapScreen({ onOpenCompare }: { onOpenCompare?: () => void } = {}) {
  const { user_profile } = useProfile();
  const breakdown = useRisingSignBreakdown();
  const [openDiscuss, setOpenDiscuss] = useState<
    null | "events" | "personality" | "appearance"
  >(null);
  if (!user_profile) return null;
  // Level 1 (time the window) once a rising sign is COMMITTED, or once the window is naturally narrow.
  // The lock matters because a high-latitude sign window can exceed FINE_DIVISIONAL_MAX_SPAN_SECONDS.
  const narrow =
    user_profile.rising_sign_locked === true ||
    search_span_seconds(user_profile.search ?? {}) <=
      FINE_DIVISIONAL_MAX_SPAN_SECONDS;
  // Owner-specified order: sticky heatmap → moon-sign note → top signs → Add-information → Saved table.
  const addPanel = <AddInformationPanel onOpen={setOpenDiscuss} />;
  return (
    <div className="page2">
      {narrow ? (
        <ExactTimeHeatmap
          moonNote={<MoonSignNote />}
          belowBars={addPanel}
          onOpenCompare={onOpenCompare}
        />
      ) : (
        <RisingSignHeatmap
          moonNote={<MoonSignNote />}
          belowBars={addPanel}
          signs={breakdown.signs}
          dayBands={breakdown.dayBands}
          eventLabels={breakdown.eventLabels}
          loadError={breakdown.loadError}
          onRetryLoad={breakdown.retry}
          dashaConcentration={breakdown.dashaConcentration}
          onOpenCompare={onOpenCompare}
        />
      )}

      <section
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          marginTop: 6,
        }}
      >
        <Expander
          summary={
            <PanelSummary
              no="00"
              title="Life-periods (eras)"
              meta="say what each era was about — fills the gaps your events can't"
            />
          }
        >
          <ErasPanel />
        </Expander>
        <Expander
          summary={
            <PanelSummary
              no="01"
              title="Saved information"
              meta="events & traits — best-matched sign + influence"
            />
          }
        >
          <SavedInfoTable signs={breakdown.signs} />
        </Expander>
        <Expander
          summary={
            <PanelSummary
              no="02"
              title="Understanding the rising signs"
              meta="classical signatures for all twelve"
            />
          }
        >
          <SignsReference />
        </Expander>
      </section>

      {openDiscuss === "events" && (
        <Modal title="Discuss life events" onClose={() => setOpenDiscuss(null)}>
          <EventsPanelBody />
        </Modal>
      )}
      {openDiscuss === "appearance" && (
        <Modal title="Discuss appearance" onClose={() => setOpenDiscuss(null)}>
          <TraitChatPanel target="lagna" />
        </Modal>
      )}
      {openDiscuss === "personality" && (
        <Modal
          title="Discuss personality traits"
          onClose={() => setOpenDiscuss(null)}
        >
          <TraitChatPanel target="moon" />
        </Modal>
      )}

      <p className="footer-note">
        Confidence is reported as a window first; an exact second is claimed
        only when the evidence earns it. This navigator is a presentation layer
        over the deterministic Jyotish engine — it never invents a score.
      </p>
    </div>
  );
}

/**
 * Topbar chip opening the marked-times comparison page — visible on BOTH heatmap levels once at
 * least one time has been marked (the user marks times while drilled in, then returns to the
 * broad rising-sign view and opens the comparison from there).
 */
function CompareTimesButton({ onOpen }: { onOpen?: () => void }) {
  const { user_profile } = useProfile();
  const count = normalize_marked_times(user_profile ?? {}).length;
  if (!count || !onOpen) return null;
  return (
    <button
      className="mono compare-open-btn"
      onClick={onOpen}
      title="Open the side-by-side comparison of the times you marked as likely"
      style={{
        border: "1px solid var(--accent)",
        color: "var(--accent)",
        background: "transparent",
        borderRadius: 8,
        padding: "4px 10px",
        cursor: "pointer",
      }}
    >
      ★ Compare times ({count})
    </button>
  );
}

/**
 * Post-intake search-window editor ("🕐 Edit window" on both heatmap levels). The intake fix for a
 * mis-entered window (e.g. 02:00→14:00 entered when the birth was actually 14:00→02:00 overnight)
 * without recreating the whole profile. Two modes mirroring intake: BETWEEN two clock times (an
 * end at/before the start rolls to the next day automatically — the overnight case; an explicit
 * "+N days" makes it a multi-day/induced search) and AROUND a time ± hours (clamped to the birth
 * day, like intake). Applying goes through `replace_search_window` (json_store): the window is
 * rebuilt with the SAME math as intake, certainty becomes "range" (ranking-neutral), and the sign
 * lock / zoom history / refinement loop / stored scores are all reset — everything derived from
 * the wrong window must not survive it.
 */
function WindowEditor({
  pending,
  onApply,
}: {
  pending: boolean;
  onApply: (start: PlainTime, end: PlainTime, endDate: PlainDate) => void;
}) {
  const { user_profile } = useProfile();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"between" | "around">("between");
  const search = user_profile?.search ?? {};
  const [start, setStart] = useState<string>(() => String(search.start ?? "00:00:00").slice(0, 5));
  const [end, setEnd] = useState<string>(() => String(search.end ?? "23:59:59").slice(0, 5));
  const [extraDays, setExtraDays] = useState<string>("auto");
  const [center, setCenter] = useState<string>("12:00");
  const [pmHours, setPmHours] = useState<string>("3");
  const birthDateStr = user_profile?.birth?.date ?? null;

  // Resolve the inputs to (start, end, endDate) with the SAME semantics intake uses.
  const resolved = useMemo((): { start: PlainTime; end: PlainTime; dayOffset: number } | { error: string } => {
    if (!birthDateStr) return { error: "No birth date on the profile." };
    if (mode === "between") {
      const s = tryParseISOTime(start);
      const e = tryParseISOTime(end);
      if (!s || !e) return { error: "Enter both a start and an end time." };
      const sSec = s.hour * 3600 + s.minute * 60 + s.second;
      const eSec = e.hour * 3600 + e.minute * 60 + e.second;
      // "auto": an end at/before the start means the window runs into the NEXT day (overnight) —
      // the exact 14:00→02:00 mis-entry case. An explicit day count wins when chosen.
      const dayOffset =
        extraDays === "auto" ? (eSec <= sSec ? 1 : 0) : Math.max(0, Math.min(3, Number(extraDays)));
      if (dayOffset === 0 && eSec <= sSec) {
        return { error: "The end must be after the start on the same day (or let it roll overnight)." };
      }
      return { start: s, end: e, dayOffset };
    }
    const c = tryParseISOTime(center);
    if (!c) return { error: "Enter the centre time." };
    const half = Number(pmHours);
    if (!Number.isFinite(half) || half <= 0) return { error: "± hours must be greater than zero." };
    // Clamp to the birth day, exactly like intake's "around" mode.
    const cSec = c.hour * 3600 + c.minute * 60 + c.second;
    const sSec = Math.max(0, cSec - half * 3600);
    const eSec = Math.min(24 * 3600 - 1, cSec + half * 3600);
    const toTime = (sec: number) =>
      plainTime(Math.floor(sec / 3600), Math.floor((sec % 3600) / 60), Math.floor(sec % 60));
    return { start: toTime(sSec), end: toTime(eSec), dayOffset: 0 };
  }, [mode, start, end, extraDays, center, pmHours, birthDateStr]);

  const fmt = (t: PlainTime) =>
    `${String(t.hour).padStart(2, "0")}:${String(t.minute).padStart(2, "0")}`;
  const preview =
    "error" in resolved
      ? null
      : `${fmt(resolved.start)} → ${fmt(resolved.end)}${
          resolved.dayOffset > 0
            ? resolved.dayOffset === 1
              ? " (next day — overnight)"
              : ` (+${resolved.dayOffset} days)`
            : ""
        }`;

  if (!birthDateStr) return null;
  if (!open) {
    return (
      <button
        className="mono"
        onClick={() => setOpen(true)}
        disabled={pending}
        title="Change the search window (fix a mis-entered time range) — resets the scan over the new window"
        style={{
          border: "1px solid var(--line)",
          color: "var(--ink2)",
          background: "transparent",
          borderRadius: 8,
          padding: "4px 10px",
          cursor: "pointer",
        }}
      >
        🕐 Edit window
      </button>
    );
  }
  const inputStyle: CSSProperties = {
    background: "var(--bg2, transparent)",
    color: "var(--ink)",
    border: "1px solid var(--line)",
    borderRadius: 6,
    padding: "3px 6px",
    fontFamily: "var(--mono)",
    fontSize: 12.5,
  };
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
        border: "1px solid var(--line)",
        borderRadius: 8,
        padding: "6px 10px",
      }}
    >
      <select value={mode} onChange={(e) => setMode(e.target.value as any)} style={inputStyle}>
        <option value="between">Between two times</option>
        <option value="around">Around a time (±)</option>
      </select>
      {mode === "between" ? (
        <>
          <input type="time" value={start} onChange={(e) => setStart(e.target.value)} style={inputStyle} />
          <span style={{ color: "var(--ink3)" }}>→</span>
          <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} style={inputStyle} />
          <select
            value={extraDays}
            onChange={(e) => setExtraDays(e.target.value)}
            style={inputStyle}
            title="How many days later the window ends. 'auto' rolls to the next day when the end time is at/before the start (overnight)."
          >
            <option value="auto">auto day</option>
            <option value="0">same day</option>
            <option value="1">+1 day</option>
            <option value="2">+2 days</option>
            <option value="3">+3 days</option>
          </select>
        </>
      ) : (
        <>
          <input type="time" value={center} onChange={(e) => setCenter(e.target.value)} style={inputStyle} />
          <span style={{ color: "var(--ink3)" }}>±</span>
          <input
            type="number"
            min={1}
            max={12}
            value={pmHours}
            onChange={(e) => setPmHours(e.target.value)}
            style={{ ...inputStyle, width: 52 }}
          />
          <span style={{ color: "var(--ink3)" }}>h</span>
        </>
      )}
      <span className="caption" style={{ color: "error" in resolved ? "var(--danger-ink, #e07a7a)" : "var(--ink3)" }}>
        {"error" in resolved ? resolved.error : preview}
      </span>
      <button
        className="mono"
        disabled={pending || "error" in resolved}
        onClick={() => {
          if ("error" in resolved) return;
          const bd = parseISODate(birthDateStr);
          onApply(resolved.start, resolved.end, addDays(bd, resolved.dayOffset));
          setOpen(false);
        }}
        style={{
          border: "1px solid var(--accent)",
          color: "var(--accent)",
          background: "transparent",
          borderRadius: 8,
          padding: "4px 10px",
          cursor: "pointer",
        }}
      >
        Apply & rescan
      </button>
      <button
        className="mono"
        onClick={() => setOpen(false)}
        style={{
          border: "none",
          color: "var(--ink3)",
          background: "transparent",
          cursor: "pointer",
        }}
      >
        Cancel
      </button>
    </div>
  );
}

/** The "Add information" panel directly under the heatmap — three buttons that open chat overlays. */
function AddInformationPanel({
  onOpen,
}: {
  onOpen: (which: "events" | "personality" | "appearance") => void;
}) {
  return (
    <div className="add-info-panel">
      <div className="add-info-buttons">
        <button className="add-info-btn" onClick={() => onOpen("events")}>
          📅 Discuss Life Events
        </button>
        <button className="add-info-btn" onClick={() => onOpen("personality")}>
          🧠 Discuss Personality Traits
        </button>
        <button className="add-info-btn" onClick={() => onOpen("appearance")}>
          🪞 Discuss Appearance
        </button>
      </div>
      <p className="caption add-info-note">
        The more I know about you, the better I can identify your birth time.
      </p>
    </div>
  );
}

// ── Moon-sign note (rising-sign-invariant psychology) ─────────────────────────────────────────

function MoonSignNote() {
  const { user_profile, runOp } = useProfile();
  const [inv, setInv] = useState<any | null | undefined>(undefined); // undefined = loading
  const loadedSig = useRef<string | null>(null);
  const generation = useRef(0);
  const sig = JSON.stringify({
    birth: user_profile?.birth,
    search: user_profile?.search,
  });

  useEffect(() => {
    if (loadedSig.current === sig) return;
    loadedSig.current = sig;
    // Generation token: two quick sig changes must not let the SLOWER response land last.
    const token = ++generation.current;
    runOp("psychology_invariants")
      .then((res) => {
        if (generation.current === token) setInv(res.result ?? null);
      })
      .catch(() => {
        if (generation.current !== token) return;
        setInv(null);
        loadedSig.current = null; // a transient failure retries on the next evidence change
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  if (inv === undefined || !inv) return null;
  const segments: any[] = inv.moon_segments ?? [];

  return (
    <section
      className="card"
      style={{
        background: "linear-gradient(120deg,var(--bg2),var(--bg3))",
        marginTop: 22,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 18,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            flex: "0 0 auto",
          }}
        >
          <div className="moon-orb" />
          <div>
            <div className="eyebrow" style={{ letterSpacing: "0.18em" }}>
              Rising-sign invariant
            </div>
            <div style={{ fontFamily: "var(--serif)", fontSize: 18 }}>
              The Moon doesn't move with your guess
            </div>
          </div>
        </div>
        <p
          style={{
            margin: 0,
            flex: 1,
            minWidth: 280,
            fontSize: 13.5,
            lineHeight: 1.6,
            color: "var(--ink2)",
            maxWidth: 560,
          }}
        >
          “This sign <em style={{ color: "var(--ink)" }}>feels</em> like me” is
          almost always the{" "}
          <strong style={{ color: "var(--ink)" }}>Moon</strong> — the mind — not
          the Ascendant. It barely moves across the birth-time range, so it
          can't decide the lagna. Don't pick a band because its personality
          feels familiar; let the dated events decide.
        </p>
      </div>
      <div className="moon-segments">
        {inv.moon_stable ? (
          <div className="moon-seg">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ fontFamily: "var(--serif)", fontSize: 16 }}>
                Moon in {inv.moon_sign}
              </div>
              <div
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: 11,
                  color: "var(--ink3)",
                }}
              >
                whole range
              </div>
            </div>
            {inv.moon_temperament && (
              <div
                style={{
                  fontSize: 12.5,
                  color: "var(--ink2)",
                  lineHeight: 1.55,
                  marginTop: 6,
                }}
              >
                Inwardly {inv.moon_temperament}.
              </div>
            )}
          </div>
        ) : (
          segments.map((seg, i) => (
            <div className="moon-seg" key={i}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ fontFamily: "var(--serif)", fontSize: 16 }}>
                  Moon in {seg.sign}
                </div>
                <div
                  style={{
                    fontFamily: "var(--mono)",
                    fontSize: 11,
                    color: "var(--ink3)",
                  }}
                >
                  {seg.from}–{seg.to}
                </div>
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  color: "var(--ink2)",
                  lineHeight: 1.55,
                  marginTop: 6,
                }}
              >
                {seg.temperament ? `Inwardly ${seg.temperament}. ` : ""}
                The Vimshottari balance also flips across this boundary — a
                genuine birth-time narrowing clue.
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

// ── Level 0 — rising-sign heatmap ─────────────────────────────────────────────────────────────

interface SignRow {
  sign: string;
  window_start: string;
  window_end: string;
  best_event_fit: number | null;
  best_era_fit: number | null;
  appearance_match: number | null;
  personality_match: number | null;
  best_time?: string | null;
  events?: Record<string, number>;
  explained_fit?: number | null; // weighted % of events scoring >50 at the sign's best moment
  explained_count?: number | null;
  events_total?: number | null;
}

/** A multi-day strip band: a SignRow for ONE day, positioned by absolute seconds on the N-day axis. */
interface DayBand extends SignRow {
  date: string; // ISO day this band belongs to
  day_index: number; // whole days from the window's start date
  abs_start: number; // absolute seconds from the start date's midnight
  abs_end: number;
}

/**
 * THE ONE DEFINITION of each per-sign signal — every surface (graph bars, leader card, Top-fits,
 * disagreement banner, Scores panel, hover) reads through these so the numbers can never drift:
 *   • Events fit  = best_event_fit    (how well the dated events time out under this sign)
 *   • Appearance  = appearance_match  (physical resemblance → the rising sign)
 *   • Personality = personality_match (disposition resemblance → the MOON sign; flat across the
 *                                      strip on a steady-Moon day — it doesn't pick the lagna)
 *   • Explains    = explained_fit     (weighted % of events scored ABOVE NEUTRAL at the sign's
 *                                      best moment — the acceptance rule made visible; replaced
 *                                      the per-sign holdout Accuracy 2026-07-20, which measured
 *                                      re-rectification flexibility and ranked the TRUE sign
 *                                      worse than chance on the reference charts)
 * `totalScore` (the even average of the available signals) is only a coarse band-colour fallback for
 * surfaces that don't pick a metric. The "most likely lagna" RANKING is `derivedScore`, a weighted
 * blend of all present signals (Events ×1.0, Explains ×0.5, Eras ×0.3, Appearance/Personality ×0.2);
 * the Level-0 heatmap strip ranks/colours by the selected metric (default Events), not by this average.
 */
function eventsFit(s: SignRow): number | null {
  return s.best_event_fit;
}
/** Eras fit — the answered life-phase ("era") signal, scored as its own per-sign band. */
function eraFit(s: SignRow): number | null {
  return s.best_era_fit;
}
function appearanceFit(s: SignRow): number | null {
  return s.appearance_match;
}
function personalityFit(s: SignRow): number | null {
  return s.personality_match;
}
/** The one Explains accessor — its own signal, shown only in the Explains slots. */
function explainedFit(s: SignRow): number | null {
  return s.explained_fit ?? null;
}
/** "6/7" display text for the Explains slots ("—" when unavailable). */
function explainedCountText(s: SignRow): string {
  return s.explained_count != null && s.events_total != null
    ? `${s.explained_count}/${s.events_total}`
    : "—";
}
/** Single % formatter so every surface renders a signal identically ("75%" / "—"). */
export function fmtPct(v: number | null | undefined): string {
  return v != null ? `${v.toFixed(1)}%` : "—";
}
function totalScore(s: SignRow): number | null {
  // Every signal that feeds the ranking (derivedScore) is in the even average — including eras
  // when answered — so a band's printed % is built from the same inputs that rank it.
  const vals = [
    s.best_event_fit,
    s.best_era_fit,
    s.appearance_match,
    s.personality_match,
    s.explained_fit,
  ].filter((v): v is number => v != null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

/**
 * Derived score — the "Most likely lagna" ranking value: a WEIGHTED BLEND of every available signal,
 * normalised over whichever signals are present for the sign. Events lead (the sharpest evidence),
 * then Explains (does this sign's best moment actually explain the events — measured 2026-07-20 to
 * IMPROVE the reference-chart ranking where the old holdout Accuracy degraded it), then the answered
 * eras, with appearance/personality contributing least (weak, Barnum-prone). A signal that is missing
 * for a sign is simply dropped from both numerator and denominator, so it neither helps nor hurts.
 *
 *   Derived = (Events·1.0 + Explains·0.5 + Eras·0.3 + Appearance·0.2 + Personality·0.2) / Σ(weights present)
 *
 * NOTE (honesty caveat): the per-sign signals often sit in a narrow band, so a blend can let a noisy
 * lower-weight signal nudge a thin lead. The events-vs-appearance disagreement banner still flags when
 * the channels diverge, so a blended leader that contradicts the raw events stays visible, not hidden.
 */
const DERIVED_WEIGHTS = {
  events: 1.0,
  explains: 0.5,
  eras: 0.3,
  appearance: 0.2,
  personality: 0.2,
} as const;
function derivedScore(s: SignRow): number | null {
  const parts: Array<[number | null, number]> = [
    [eventsFit(s), DERIVED_WEIGHTS.events],
    [explainedFit(s), DERIVED_WEIGHTS.explains],
    [eraFit(s), DERIVED_WEIGHTS.eras],
    [appearanceFit(s), DERIVED_WEIGHTS.appearance],
    [personalityFit(s), DERIVED_WEIGHTS.personality],
  ];
  const present = parts.filter((p): p is [number, number] => p[0] != null);
  if (!present.length) return null;
  const wsum = present.reduce((a, [, w]) => a + w, 0);
  return present.reduce((a, [v, w]) => a + v * w, 0) / wsum;
}

/** Highest-scoring sign for a signal (Events/Appearance/Personality), ignoring signs that lack it. */
function leaderBy(
  signs: SignRow[],
  signal: (s: SignRow) => number | null,
): SignRow | undefined {
  return [...signs]
    .filter((s) => signal(s) != null)
    .sort((a, b) => signal(b)! - signal(a)!)[0];
}

/**
 * Shared rising-sign breakdown fetch — lifted to the page so BOTH the heatmap and the Saved-info
 * influence table read the same per-sign rows (events-per-sign for "best matched sign", windows,
 * appearance/personality/holdout). Re-fetches whenever evidence, per-item influence, or the search
 * window changes.
 */
type DashaConcentration = {
  total: number;
  distinct: number;
  dominant_lord: string;
  dominant_count: number;
  concentrated: boolean;
};
function useRisingSignBreakdown(): {
  signs: SignRow[] | null;
  dayBands: DayBand[] | null;
  eventLabels: string[];
  loadError: string | null;
  dashaConcentration: DashaConcentration | null;
  retry: () => void;
} {
  const { user_profile, runOp } = useProfile();
  const [signs, setSigns] = useState<SignRow[] | null>(null);
  const [dayBands, setDayBands] = useState<DayBand[] | null>(null);
  const [eventLabels, setEventLabels] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dashaConcentration, setDashaConcentration] =
    useState<DashaConcentration | null>(null);
  // Bumped by the error banner's Retry — re-runs the effect for the same evidence signature.
  const [retryNonce, setRetryNonce] = useState(0);
  const loadedSig = useRef<string | null>(null);
  const generation = useRef(0);
  const sig = JSON.stringify({
    events: user_profile?.events,
    era: user_profile?.era_answers,
    traits: user_profile?.traits,
    rejected: user_profile?.rejected_traits,
    influence: user_profile?.trait_influence,
    likelihood: user_profile?.rising_sign_likelihood,
    search: user_profile?.search,
  });
  useEffect(() => {
    if (loadedSig.current === sig) return;
    loadedSig.current = sig;
    const token = ++generation.current;
    const live = () => generation.current === token;
    setSigns(null);
    setLoadError(null);
    (async () => {
      const breakdownRes = await runOp("rising_sign_breakdown", {
        appearance_likelihood: user_profile?.rising_sign_likelihood ?? null,
      });
      const breakdown = breakdownRes.result;
      if (!breakdown?.available || !breakdown?.signs?.length) {
        if (live()) setLoadError("not_available");
        return;
      }
      const rows: SignRow[] = breakdown.signs;
      if (!live()) return;
      // The Explains signal rides on the breakdown rows themselves (explained_fit — computed at
      // each sign's best moment inside `rising_sign_breakdown`), so there is no second worker op.
      // The per-sign holdout run that used to follow here was REMOVED 2026-07-20: measured on the
      // reference charts its per-fold re-rectification ranked the TRUE sign worse than chance
      // (avg 7.75/12), so it must not feed the per-sign ranking; the profile-level
      // `holdout_validation` chip on the Level-1 view remains the honest holdout surface.
      setSigns(rows);
      setDayBands(breakdown.day_bands ?? null);
      setEventLabels(breakdown.event_labels ?? []);
      setDashaConcentration(breakdown.dasha_concentration ?? null);
    })().catch((e) => {
      // Only the LIVE run may report failure and clear the stamp — a superseded run must not
      // wipe the newer run's signature (that would trigger a spurious refetch).
      if (live()) {
        setLoadError(String(e?.message ?? e));
        loadedSig.current = null;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, retryNonce]);
  return {
    signs,
    dayBands,
    eventLabels,
    loadError,
    dashaConcentration,
    retry: () => setRetryNonce((n) => n + 1),
  };
}

function RisingSignHeatmap({
  moonNote,
  belowBars,
  signs,
  dayBands,
  eventLabels,
  loadError,
  onRetryLoad,
  dashaConcentration,
  onOpenCompare,
}: {
  moonNote?: ReactNode;
  belowBars?: ReactNode;
  signs: SignRow[] | null;
  dayBands?: DayBand[] | null;
  eventLabels: string[];
  loadError: string | null;
  onRetryLoad?: () => void;
  dashaConcentration?: DashaConcentration | null;
  onOpenCompare?: () => void;
}) {
  const { user_profile, runOp, reload, mutate } = useProfile();
  // The sign whose detail panel is OPEN — set by CLICKING a bar (hover only shows the time range in
  // the tooltip). The panel then offers Close or "Zoom into this rising sign" (the drill-down).
  const [selected, setSelected] = useState<SignRow | null>(null);
  const [metric, setMetric] = useState<MetricKey>("events");
  const narrowAction = useAsyncAction();

  if (loadError && loadError !== "not_available")
    return (
      <Banner kind="error">
        {loadError}{" "}
        {onRetryLoad && (
          <button className="mono" onClick={onRetryLoad}>
            ↻ Retry
          </button>
        )}
      </Banner>
    );
  if (loadError === "not_available")
    return (
      <>
        <Banner kind="info">
          The heatmap needs a search window with birth details. Add a dated
          event below to start lighting it up.
        </Banner>
        {moonNote}
      </>
    );
  if (!signs)
    return (
      <>
        <LoadingLine message="Splitting the day into rising signs and scoring each against your evidence…" />
        {moonNote}
      </>
    );

  // Signs the user has struck out entirely (e.g. "Aries doesn't fit me at all") are removed from
  // ALL consideration — the bar strip, the leaders, the ranking and the Scores table. Excluding is
  // a pure presentation filter (it never re-scans), so the cross is instant and fully reversible.
  const excluded: string[] = user_profile?.excluded_rising_signs ?? [];
  const excludedSet = new Set(excluded);
  const visibleSigns = signs.filter((s) => !excludedSet.has(s.sign));
  const excludedRows = signs.filter((s) => excludedSet.has(s.sign));
  const setExcluded = (next: string[]) =>
    mutate((p: any) => {
      p.excluded_rising_signs = next;
    });
  const excludeSign = (sign: string) =>
    setExcluded([...excluded.filter((x) => x !== sign), sign]);
  const restoreSign = (sign: string) =>
    setExcluded(excluded.filter((x) => x !== sign));

  // ONE events leader + ONE traits leader, by the same eventsFit/traitsFit used everywhere — so
  // the Top-fits chips, the disagreement banner and the graph's tallest bar always name the SAME
  // sign. (Both filter out signs lacking the signal.)
  const eventsLeader = leaderBy(visibleSigns, eventsFit);
  const appearanceLeader = leaderBy(visibleSigns, appearanceFit);
  const personalityLeader = leaderBy(visibleSigns, personalityFit);

  async function commit(selectedRow: SignRow) {
    const signName = selectedRow.sign;
    // A clicked multi-day band carries its OWN day (`date`); commit to exactly that day's window.
    // A normal (single-day or aggregate) row resolves the day from its best_time.
    const dayBand = (selectedRow as Partial<DayBand>).date ? (selectedRow as DayBand) : null;
    const window: [string, string] | null = dayBand
      ? [dayBand.window_start, dayBand.window_end]
      : (sign_rising_window(user_profile!, signName) ??
        (selectedRow ? [selectedRow.window_start, selectedRow.window_end] : null));
    if (!window || !window[0] || !window[1]) {
      narrowAction.setError(
        `Could not compute the ${signName} window for this date/place.`,
      );
      return;
    }
    // On a MULTI-DAY (induced-delivery) search the same sign rises every day; the dated events pick
    // the best day. A clicked day-band commits to ITS day; otherwise we take the day from best_time.
    // The sign's rising window can cross midnight, so the end date rolls over when end precedes start.
    const bestDay = dayBand ? dayBand.date : (selectedRow?.best_time ? dateOfLabel(selectedRow.best_time) : null);
    const crossesMidnight = secondsOfLabel(window[1]) <= secondsOfLabel(window[0]);
    await narrowAction.run(
      `Zooming into ${signName} (${shortTime(window[0])}–${shortTime(window[1])})…`,
      async () => {
        mutate((p: any) => {
          const sr = (p.search ??= {});
          // Remember the window we're committing FROM (the full, possibly multi-day, search) so
          // zoom-out restores it instead of collapsing to a single birth-day. Captured only at the
          // top-level commit (Level 0), which is always the base window.
          p.base_window = {
            start: sr.start,
            end: sr.end,
            start_date: sr.start_date ?? p.birth?.date ?? null,
            end_date: sr.end_date ?? p.birth?.date ?? null,
          };
          // Captured BEFORE the window is overwritten — the committed band's dates are resolved
          // relative to the window it was carved from.
          const base = {
            multiday: searchIsMultiday(sr),
            start: (sr.start ?? null) as string | null,
            start_date: (sr.start_date ?? p.birth?.date ?? null) as string | null,
          };
          [sr.start, sr.end] = window;
          if (bestDay) {
            sr.start_date = bestDay;
            sr.end_date = crossesMidnight
              ? formatISODate(addDays(parseISODate(bestDay), 1))
              : bestDay;
          } else {
            // Single-day/overnight commit (bestDay is null for every non-multi-day search): the
            // dates must follow the committed band, or an overnight base (21:00→01:00, D/D+1)
            // committed to a non-crossing band keeps end_date=D+1 → a phantom ~25h "multi-day"
            // window that scans coarse forever and never reaches D60.
            renormalizeWindowDates(sr, base);
          }
          p.loop_count = 0;
          // LOCK the level switch to this commit, not to the window SPAN: a high-latitude sign can rise
          // for >2.5h (> FINE_DIVISIONAL_MAX_SPAN_SECONDS), which would otherwise leave the page on the
          // Level-0 rising-sign view showing a single useless bar instead of advancing to timing.
          p.rising_sign_locked = true;
          // Fresh Level-1 session at the committed window — no drill history yet.
          p.zoom_stack = [];
        });
        await runOp("run_internal_matching");
        reload();
      },
    );
  }

  // The strip spans ONLY the current search window (e.g. 10:00–14:00), not the whole day, so the
  // rising-sign bands fill the graph instead of being squeezed into a sliver of a 24h axis. Each
  // band is placed as a fraction of the window, measured FORWARD from the window start — which also
  // handles an overnight window (e.g. 21:00→01:00) without splitting: a band that crosses midnight
  // is still a single forward span from the window start.
  const DAY_SECONDS = 86400;
  const searchWin = user_profile?.search ?? {};
  // A MULTI-DAY (induced-delivery) window spans more than 24h. The ascendant cycle repeats each day,
  // so Level 0 still shows the 12 rising-sign bands on a single 24h time-of-day axis; the dated
  // events decide WHICH day, surfaced when the user commits a sign (commit() carries the winning
  // day into the narrowed window). So render multi-day as a full-day axis with a date-range banner.
  const winSpanSecs =
    searchWin.start_date && searchWin.end_date
      ? diffDays(parseISODate(searchWin.end_date), parseISODate(searchWin.start_date)) * DAY_SECONDS +
        (secondsOfLabel(searchWin.end ?? "23:59:59") - secondsOfLabel(searchWin.start ?? "00:00:00"))
      : forwardWidthSeconds(searchWin.start ?? "00:00:00", searchWin.end ?? "23:59:59");
  const multiday = winSpanSecs > DAY_SECONDS;
  // When a multi-day breakdown is available we lay the days out LEFT-TO-RIGHT on a single LINEAR
  // axis (each day showing its own rising-sign bands, coloured by THAT day's score), so the days
  // are actually visible. Falls back to the 24h sign axis if day_bands didn't load.
  const visibleDayBands = (dayBands ?? []).filter((b) => !excludedSet.has(b.sign));
  const multidayStrip = multiday && visibleDayBands.length > 0;
  const winStartAbs = secondsOfLabel(searchWin.start ?? "00:00:00");
  const winStartLabel = multiday ? "00:00:00" : (searchWin.start ?? "00:00:00");
  const winEndLabel = multiday ? "23:59:59" : (searchWin.end ?? "23:59:59");
  const winStart = secondsOfLabel(winStartLabel);
  const winWidth = multiday ? DAY_SECONDS : Math.max(forwardWidthSeconds(winStartLabel, winEndLabel), 1);
  const isFullDay = winWidth >= DAY_SECONDS - 60; // ~the whole day → keep the "Full day" framing
  const dayCount = multiday ? Math.ceil(winSpanSecs / DAY_SECONDS) : 1;
  const forwardFromStart = (sec: number) =>
    (((sec - winStart) % DAY_SECONDS) + DAY_SECONDS) % DAY_SECONDS;

  // Day boundary markers for the multi-day linear axis (one label per calendar day in the window).
  const dayMarkers: { label: string; leftFrac: number }[] = [];
  if (multidayStrip && searchWin.start_date) {
    const span = Math.max(winSpanSecs, 1);
    const sd0 = parseISODate(searchWin.start_date);
    for (let d = 0; d <= dayCount; d++) {
      const abs = d * DAY_SECONDS; // midnight of day d (absolute from start date's midnight)
      const frac = (abs - winStartAbs) / span;
      if (frac < -0.001 || frac > 1.001) continue;
      dayMarkers.push({ label: shortDay(addDays(sd0, d)), leftFrac: Math.max(0, Math.min(1, frac)) });
    }
  }

  const bars: Bar[] = multidayStrip
    ? visibleDayBands.map((b) => {
        const span = Math.max(winSpanSecs, 1);
        const ss = totalScore(b);
        const relA = Math.max(0, Math.min(b.abs_start - winStartAbs, span));
        const relB = Math.max(0, Math.min(b.abs_end - winStartAbs, span));
        return {
          // abs_start makes the key unique even when a sign has two runs on one day (the sign rising
          // across midnight appears at both ends of a calendar day).
          key: `${b.sign}@${b.abs_start}`,
          score: ss,
          name: b.sign,
          sub: shortDay(parseISODate(b.date)),
          pct: ss != null ? `${ss.toFixed(1)}%` : "—",
          metrics: {
            events: eventsFit(b),
            eras: eraFit(b),
            appearance: appearanceFit(b),
            personality: personalityFit(b),
            explains: explainedFit(b),
            derived: derivedScore(b),
          },
          disabled: narrowAction.pending,
          cursor: "pointer",
          hint: `${shortDay(parseISODate(b.date))} · ${shortTime(b.window_start)}–${shortTime(b.window_end)}`,
          onClick: () => setSelected(b),
          leftFrac: relA / span,
          widthFrac: Math.max((relB - relA) / span, 0.004),
        };
      })
    : visibleSigns.map((s) => {
        const ss = totalScore(s);
        let relA = forwardFromStart(secondsOfLabel(s.window_start));
        let relB = forwardFromStart(secondsOfLabel(s.window_end));
        if (relB < relA) relB += DAY_SECONDS; // band straddles the window-start wrap
        relA = Math.max(0, Math.min(relA, winWidth));
        relB = Math.max(0, Math.min(relB, winWidth));
        return {
          key: s.sign,
          score: ss,
          name: s.sign,
          sub: SANSKRIT[s.sign] ?? "",
          pct: ss != null ? `${ss.toFixed(1)}%` : "—",
          metrics: {
            events: eventsFit(s),
            eras: eraFit(s),
            appearance: appearanceFit(s),
            personality: personalityFit(s),
            explains: explainedFit(s),
            derived: derivedScore(s),
          },
          disabled: narrowAction.pending,
          cursor: "pointer",
          hint: `${shortTime(s.window_start)}–${shortTime(s.window_end)}`,
          // Click OPENS the detail panel (no longer commits directly); the panel's button zooms in.
          onClick: () => setSelected(s),
          leftFrac: relA / winWidth,
          widthFrac: Math.max((relB - relA) / winWidth, 0.002),
        };
      });

  return (
    <>
      <div className="heatmap-sticky">
        <div className="heatmap-panel">
          <div className="heatmap-topbar">
            <div className="crumbs">
              <button className="here">
                {multiday
                  ? `${dayCount} days · ${searchWin.start_date} → ${searchWin.end_date}`
                  : isFullDay
                    ? "Full day"
                    : `${shortTime(winStartLabel)}–${shortTime(winEndLabel)}`}
              </button>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <Legend rank />
              <WindowEditor
                pending={narrowAction.pending}
                onApply={(startT, endT, endDate) =>
                  narrowAction.run("Rescanning the corrected window…", async () => {
                    // mutate saves locally AND pushes to DynamoDB — the correction is permanent.
                    mutate((p: any, r: any) =>
                      replace_search_window(p, startT, endT, parseISODate(p.birth.date), endDate, r),
                    );
                    await runOp("run_internal_matching");
                    reload();
                  })
                }
              />
              <CompareTimesButton onOpen={onOpenCompare} />
            </div>
          </div>

          {dashaConcentration?.concentrated && (
            <div
              style={{
                margin: "0 0 10px",
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid var(--warn-line, #6b5a1f)",
                background: "var(--warn-bg, rgba(180,140,20,0.10))",
                color: "var(--ink2, #cbb98a)",
                fontSize: 13,
                lineHeight: 1.4,
              }}
            >
              ⚠️{" "}
              <b>
                {dashaConcentration.dominant_count} of{" "}
                {dashaConcentration.total} events
              </b>{" "}
              fall in the same{" "}
              <b>
                {dashaConcentration.dominant_lord.charAt(0).toUpperCase() +
                  dashaConcentration.dominant_lord.slice(1)}
              </b>{" "}
              life-period — so they mostly confirm one thing, and the sign
              estimate from events is <b>weak</b>.{" "}
              <span style={{ opacity: 0.85 }}>
                Suggested fix: add a few dated events from{" "}
                <b>other years / life-periods</b> (e.g. childhood, a different
                decade) to give the engine independent evidence.
              </span>
            </div>
          )}

          {multiday && (
            <div
              style={{
                margin: "0 0 10px",
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid var(--accent2-line, #3a5a6b)",
                background: "var(--accent2-bg, rgba(40,120,180,0.10))",
                color: "var(--ink2, #9ec5d8)",
                fontSize: 13,
                lineHeight: 1.4,
              }}
            >
              🗓️ Searching <b>{dayCount} days</b> ({searchWin.start_date} →{" "}
              {searchWin.end_date}) — useful for induced or scheduled deliveries where birth
              happened earlier than planned. The days are laid out left-to-right, each showing its
              rising-sign bands; the rising-sign cycle repeats daily, so it's your <b>dated events</b>{" "}
              that make one day score better than another. Click a band to commit that exact{" "}
              <b>day + rising sign</b>, then drill to the second.
            </div>
          )}

          <div className="level-caption">
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <h2>Cycle 1 — which sign was rising?</h2>
              <TopFits
                chips={[
                  {
                    label: "Top events fit",
                    value: eventsLeader ? eventsFit(eventsLeader) : null,
                    sign: eventsLeader?.sign,
                  },
                  {
                    label: "Top appearance fit",
                    value: appearanceLeader
                      ? appearanceFit(appearanceLeader)
                      : null,
                    sign: appearanceLeader?.sign,
                  },
                  {
                    label: "Top personality fit",
                    value: personalityLeader
                      ? personalityFit(personalityLeader)
                      : null,
                    sign: personalityLeader?.sign,
                  },
                ]}
              />
              <span className="sub">
                {METRIC_OPTIONS.find((m) => m.key === metric)?.help}
              </span>
            </div>
            <span className="res-note">WIDE · ~60s coarse scan</span>
          </div>

          <div
            className="metric-toggle"
            role="group"
            aria-label="Choose which metric the bars show"
          >
            {METRIC_OPTIONS.map((m) => (
              <button
                key={m.key}
                type="button"
                className={`metric-toggle-btn${metric === m.key ? " active" : ""}`}
                aria-pressed={metric === m.key}
                title={m.help}
                onClick={() => setMetric(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>

          <HeatmapBars
            bars={bars}
            label={`Rising-sign heatmap — ${METRIC_LABEL[metric]}`}
            metric={metric}
            rankColors
          />
          {multidayStrip ? (
            <DayAxis markers={dayMarkers} />
          ) : (
            <AxisTicks start={winStartLabel} end={winEndLabel} />
          )}

          <PrecisionHero
            tier="window"
            readout="—:—:—"
            range="commit a sign to begin timing"
            message={
              isFullDay
                ? "Searching all 24h — no rising sign committed yet, so only a confidence band is honest."
                : `Searching ${shortTime(winStartLabel)}–${shortTime(winEndLabel)} — no rising sign committed yet, so only a confidence band is honest.`
            }
            resolutionLabel="window"
          />
        </div>
        <HoverPanel
          sign={selected}
          pending={narrowAction.pending}
          onClose={() => setSelected(null)}
          onZoom={selected ? () => commit(selected) : undefined}
        />
      </div>

      {narrowAction.pending && <LoadingLine message={narrowAction.message} />}
      {narrowAction.error && <Banner kind="error">{narrowAction.error}</Banner>}

      {belowBars}

      {moonNote}

      <section style={{ marginTop: 22 }}>
        <div className="top-signs-head">
          <h3
            style={{
              margin: 0,
              fontFamily: "var(--serif)",
              fontWeight: 500,
              fontSize: 18,
            }}
          >
            Leading rising signs
          </h3>
          <span className="caption">
            ranked by dated events (appearance &amp; personality shown, but
            don't override the events)
          </span>
        </div>

        {excludedRows.length > 0 && (
          <div
            className="excluded-signs"
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 8,
              margin: "4px 0 12px",
            }}
          >
            <span className="caption" style={{ color: "var(--ink3)" }}>
              Excluded:
            </span>
            {excludedRows.map((s) => (
              <button
                key={s.sign}
                className="excluded-chip"
                title={`Bring ${s.sign} back into consideration`}
                onClick={() => restoreSign(s.sign)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "2px 8px",
                  borderRadius: 999,
                  border: "1px solid var(--line)",
                  background: "transparent",
                  color: "var(--ink3)",
                  fontSize: 12,
                  cursor: "pointer",
                  textDecoration: "line-through",
                }}
              >
                {s.sign}{" "}
                <span style={{ textDecoration: "none", fontWeight: 600 }}>
                  ↺
                </span>
              </button>
            ))}
          </div>
        )}

        {eventsLeader &&
          appearanceLeader &&
          appearanceLeader.sign !== eventsLeader.sign && (
            <Banner kind="warn">
              ⚖︎ Appearance leans <strong>{appearanceLeader.sign}</strong>, but
              the dated events more strongly support{" "}
              <strong>{eventsLeader.sign}</strong>. Events are the harder, more
              objective evidence — they win ties. Traits use modern astrology
              (read from the Moon sign) and are WEAK evidence — prone to the
              Barnum effect, they never pick the rising sign.
            </Banner>
          )}

        <TopSigns
          signs={visibleSigns}
          onCommit={commit}
          onExclude={excludeSign}
        />
      </section>

      <Expander
        summary={
          <PanelSummary
            no="00"
            title="Scores"
            meta="per-sign fit, windows & holdout verdicts"
          />
        }
      >
        <ScoresTable
          signs={visibleSigns}
          eventLabels={eventLabels}
          onExclude={excludeSign}
        />
      </Expander>
    </>
  );
}

/** Leader card (big serif + Sanskrit + derived fit) plus the next five as ranked bars. */
function TopSigns({
  signs,
  onCommit,
  onExclude,
}: {
  signs: SignRow[];
  onCommit: (row: SignRow) => void;
  onExclude: (sign: string) => void;
}) {
  const ranked = signs
    .map((s) => ({ s, total: derivedScore(s) }))
    .filter((x): x is { s: SignRow; total: number } => x.total != null)
    .sort((a, b) => b.total - a.total);
  if (ranked.length === 0) {
    return (
      <Card>
        <p className="muted">
          No evidence yet — add dated events or describe the person's traits
          below, and the ranking (and the heatmap) lights up.
        </p>
      </Card>
    );
  }
  const top = ranked[0];
  const runners = ranked.slice(1, 6);
  const sigSummary =
    (sign_appearance_signature(top.s.sign) as any)?.summary ?? "";
  return (
    <div className="leader-grid">
      <div className="leader-card">
        <div
          className="orbit"
          style={{ right: -30, top: -30, width: 140, height: 140 }}
        />
        <div
          className="orbit"
          style={{
            right: 6,
            top: 8,
            width: 90,
            height: 90,
            borderColor: "var(--line2)",
          }}
        />
        <div className="leader-eyebrow">Most likely lagna</div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 12,
            marginTop: 8,
          }}
        >
          <div className="leader-name">{top.s.sign}</div>
          <div className="leader-sa">{SANSKRIT[top.s.sign]}</div>
          <button
            className="sign-exclude"
            title={`Remove ${top.s.sign} from consideration`}
            aria-label={`Remove ${top.s.sign} from consideration`}
            onClick={(e) => {
              e.stopPropagation();
              onExclude(top.s.sign);
            }}
            style={{
              marginLeft: "auto",
              border: "1px solid var(--line)",
              background: "transparent",
              color: "var(--ink3)",
              borderRadius: 999,
              width: 24,
              height: 24,
              lineHeight: "1",
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            ✕
          </button>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 24,
            marginTop: 18,
          }}
        >
          <div>
            <div className="leader-blend">{top.total.toFixed(1)}%</div>
            <div className="stat-label">derived fit</div>
          </div>
          <div
            style={{
              borderLeft: "1px solid var(--line)",
              paddingLeft: 24,
              display: "flex",
              gap: 18,
            }}
          >
            <div>
              <div className="leader-stat">{fmtPct(eventsFit(top.s))}</div>
              <div className="stat-label" style={{ letterSpacing: 0 }}>
                events
              </div>
            </div>
            <div>
              <div className="leader-stat">{fmtPct(appearanceFit(top.s))}</div>
              <div className="stat-label" style={{ letterSpacing: 0 }}>
                appearance
              </div>
            </div>
            <div>
              <div className="leader-stat">{fmtPct(personalityFit(top.s))}</div>
              <div className="stat-label" style={{ letterSpacing: 0 }}>
                personality
              </div>
            </div>
            <div>
              <div className="leader-stat">{fmtPct(explainedFit(top.s))}</div>
              <div className="stat-label" style={{ letterSpacing: 0 }}>
                explains {explainedCountText(top.s)}
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: "var(--ink2)",
            lineHeight: 1.55,
            marginTop: 16,
            borderTop: "1px solid var(--line)",
            paddingTop: 13,
          }}
        >
          rises {shortTime(top.s.window_start)}–{shortTime(top.s.window_end)}
          {sigSummary ? ` · ${sigSummary}` : ""}
        </div>
      </div>

      <div className="runners">
        {runners.map(({ s, total }, i) => (
          <div key={s.sign} className="runner" onClick={() => onCommit(s)}>
            <div className="rank">{`0${i + 2}`}</div>
            <div style={{ flex: 1 }}>
              <span className="en">{s.sign}</span>
              <span className="sa">{SANSKRIT[s.sign]}</span>
            </div>
            <div className="runner-bar">
              <div
                style={{ width: `${total}%`, background: bandGradient(total) }}
              />
            </div>
            <div className="blend">{total.toFixed(1)}</div>
            <button
              className="sign-exclude"
              title={`Remove ${s.sign} from consideration`}
              aria-label={`Remove ${s.sign} from consideration`}
              onClick={(e) => {
                e.stopPropagation();
                onExclude(s.sign);
              }}
              style={{
                border: "none",
                background: "transparent",
                color: "var(--ink3)",
                cursor: "pointer",
                fontSize: 13,
                padding: "0 2px",
                marginLeft: 6,
              }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Scores panel: Rising sign | Window | Events | Traits | Explains | Derived + one column per event. */
function ScoresTable({
  signs,
  eventLabels,
  onExclude,
}: {
  signs: SignRow[];
  eventLabels: string[];
  onExclude: (sign: string) => void;
}) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="data" style={{ minWidth: 640 }}>
        <thead>
          <tr>
            <th></th>
            <th>Rising sign</th>
            <th>Window</th>
            <th
              style={{ textAlign: "right" }}
              title="How well your dated events time out under this sign"
            >
              Events
            </th>
            <th
              style={{ textAlign: "right" }}
              title="How well your answered life-periods (eras, good/hard) time out under this sign"
            >
              Eras
            </th>
            <th
              style={{ textAlign: "right" }}
              title="Physical resemblance → the rising sign (rejected traits gate it down)"
            >
              Appearance
            </th>
            <th
              style={{ textAlign: "right" }}
              title="Modern-astrology personality match → the Moon sign. WEAK evidence (Barnum-prone); same across the day unless the Moon changes sign."
            >
              Personality
            </th>
            <th
              style={{ textAlign: "right" }}
              title="How many of your dated events this sign explains (scores above neutral) at its best moment"
            >
              Explains
            </th>
            <th
              style={{ textAlign: "right" }}
              title="The lagna recommendation — equals the Events fit when dated events exist (appearance/personality never override events); a trait blend only for event-less profiles."
            >
              Derived
            </th>
            {eventLabels.map((l) => (
              <th
                key={l}
                style={{ textAlign: "right" }}
                title={`Best fit of “${l}” in this sign's window`}
              >
                {l}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {signs.map((s) => (
            <tr key={s.sign}>
              <td style={{ textAlign: "center" }}>
                <button
                  className="sign-exclude"
                  title={`Remove ${s.sign} from consideration`}
                  aria-label={`Remove ${s.sign} from consideration`}
                  onClick={() => onExclude(s.sign)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "var(--ink3)",
                    cursor: "pointer",
                    fontSize: 13,
                    padding: 0,
                  }}
                >
                  ✕
                </button>
              </td>
              <td>
                <span style={{ fontFamily: "var(--serif)", fontSize: 14 }}>
                  {s.sign}
                </span>{" "}
                <span style={{ color: "var(--ink3)", fontStyle: "italic" }}>
                  {SANSKRIT[s.sign]}
                </span>
              </td>
              <td
                className="num"
                style={{ textAlign: "left", color: "var(--ink2)" }}
              >
                {shortTime(s.window_start)}–{shortTime(s.window_end)}
              </td>
              <td className="num">{fmtPct(eventsFit(s))}</td>
              <td className="num" style={{ color: "var(--ink2)" }}>
                {fmtPct(eraFit(s))}
              </td>
              <td className="num" style={{ color: "var(--ink2)" }}>
                {fmtPct(appearanceFit(s))}
              </td>
              <td className="num" style={{ color: "var(--ink2)" }}>
                {fmtPct(personalityFit(s))}
              </td>
              <td className="num" style={{ color: "var(--ink2)" }}>
                {explainedFit(s) != null
                  ? `${explainedCountText(s)} (${explainedFit(s)!.toFixed(0)}%)`
                  : "—"}
              </td>
              <td className="num" style={{ fontWeight: 600 }}>
                {fmtPct(derivedScore(s))}
              </td>
              {eventLabels.map((l) => {
                const v = s.events?.[l];
                return (
                  <td className="num" key={l}>
                    {v != null ? v.toFixed(0) : "—"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HoverPanel({
  sign,
  onClose,
  onZoom,
  pending,
}: {
  sign: SignRow | null;
  onClose?: () => void;
  onZoom?: () => void;
  pending?: boolean;
}) {
  if (!sign)
    return (
      <div className="heatmap-hover muted">
        Hover a band to see its time range; <strong>click</strong> it to open
        the details and zoom into that rising sign.
      </div>
    );
  const ss = totalScore(sign);
  // Physical (appearance) requirements ONLY — a clean {feature: [option labels]} map for bullets.
  const appearance = Object.entries(
    (sign_appearance_signature(sign.sign, false) as any)?.by_question ?? {},
  ) as Array<[string, string[]]>;
  const explainsText =
    explainedFit(sign) != null
      ? `${explainedCountText(sign)} events (${explainedFit(sign)!.toFixed(1)}%)`
      : "—";
  const signals: Array<[string, string]> = [
    ["Events fit", fmtPct(eventsFit(sign))],
    ["Appearance", fmtPct(appearanceFit(sign))],
    ["Personality (Moon)", fmtPct(personalityFit(sign))],
    ["Explains", explainsText],
  ];
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const ul: CSSProperties = {
    margin: "5px 0 0",
    paddingLeft: 16,
    listStyleType: "disc",
  };
  const li: CSSProperties = { marginBottom: 3, lineHeight: 1.45 };
  const keyStyle: CSSProperties = { color: "var(--ink3)" };

  return (
    <div className="heatmap-hover">
      {/* Header — sign + window + band + total, on one wrapping line. */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <strong style={{ fontFamily: "var(--serif)", fontSize: 15 }}>
          {sign.sign}
        </strong>
        <span style={{ color: "var(--ink3)", fontStyle: "italic" }}>
          {SANSKRIT[sign.sign]}
        </span>
        <span style={{ color: "var(--ink2)", fontSize: 12.5 }}>
          rises {shortTime(sign.window_start)}–{shortTime(sign.window_end)}
        </span>
        <span className="band">{bandLabel(ss)}</span>
        {ss != null && (
          <span className="muted" style={{ fontSize: 12 }}>
            total {ss.toFixed(1)}% · avg of signals
          </span>
        )}
      </div>

      {/* Signals — one bullet each. */}
      <ul style={ul}>
        {signals.map(([k, v]) => (
          <li key={k} style={li}>
            <span style={keyStyle}>{k}:</span> <strong>{v}</strong>
          </li>
        ))}
      </ul>

      {/* Appearance requirements — one bullet per facial/body feature. */}
      {appearance.length > 0 && (
        <div
          style={{
            marginTop: 8,
            borderTop: "1px solid var(--line)",
            paddingTop: 7,
          }}
        >
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              letterSpacing: 0.3,
              textTransform: "uppercase",
              color: "var(--ink2)",
            }}
          >
            Requires (appearance)
          </div>
          <ul style={ul}>
            {appearance.map(([k, labels]) => (
              <li key={k} style={li}>
                <span style={keyStyle}>{cap(k)}:</span> {labels.join(" / ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions — close the panel, or zoom (drill down) into this rising sign's window. */}
      {(onClose || onZoom) && (
        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 10,
            borderTop: "1px solid var(--line)",
            paddingTop: 9,
          }}
        >
          {onZoom && (
            <button
              type="button"
              onClick={onZoom}
              disabled={pending}
              style={{
                background: "var(--accent)",
                color: "#15151b",
                border: "none",
                borderRadius: 8,
                padding: "7px 14px",
                fontWeight: 600,
                fontSize: 13,
                cursor: pending ? "default" : "pointer",
                opacity: pending ? 0.6 : 1,
              }}
            >
              🔍 Zoom into this rising sign
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "transparent",
                color: "var(--ink2)",
                border: "1px solid var(--line)",
                borderRadius: 8,
                padding: "7px 12px",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Close
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Life-periods (eras): answer what each life-period was about → narrows the rising sign ──────
interface EraSeg {
  kind: "era" | "transition";
  start: string;
  end: string;
  lord?: string;
  lords?: string[];
  span_years?: number;
}
const ERA_THEME_OPTIONS: Array<{ key: string; label: string }> = [
  { key: "career", label: "Career / work / status" },
  { key: "marriage", label: "Marriage / partner / love" },
  { key: "children", label: "Children / parenthood" },
  { key: "home", label: "Home / property / family life" },
  { key: "money", label: "Money / gains / success" },
  { key: "study", label: "Study / learning / new skill" },
  { key: "health", label: "Health / hardship / struggle" },
  { key: "loss", label: "Loss / endings / grief" },
  { key: "travel", label: "Travel / move / big change" },
];
const _cap = (l?: string) => (l ? l.charAt(0).toUpperCase() + l.slice(1) : "");

interface EraAnswer {
  start: string;
  end: string;
  theme: string;
  valence: "positive" | "negative";
}
const _eraThemeLabel = (key: string) =>
  ERA_THEME_OPTIONS.find((t) => t.key === key)?.label.split(" / ")[0] ?? key;

function ErasPanel() {
  const { user_profile, runOp, mutate } = useProfile();
  const [segments, setSegments] = useState<EraSeg[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const loadedSig = useRef<string | null>(null);
  const generation = useRef(0);
  const sig = JSON.stringify({
    birth: user_profile?.birth,
    search: user_profile?.search,
    era: user_profile?.era_answers,
  });
  useEffect(() => {
    if (loadedSig.current === sig) return;
    loadedSig.current = sig;
    const token = ++generation.current;
    setSegments(null);
    setErr(null);
    runOp("dasha_era_segments", {})
      .then((res: any) => {
        if (generation.current !== token) return;
        const segs = res?.result?.segments;
        if (Array.isArray(segs)) setSegments(segs);
        else setErr("not_available");
      })
      .catch((e: any) => {
        // Only the LIVE run may report + clear the stamp — a superseded run must not wipe the
        // newer run's signature.
        if (generation.current === token) {
          setErr(String(e?.message ?? e));
          loadedSig.current = null;
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, retryNonce]);

  // Editing model: a local DRAFT of the answer list (a period can hold several themed +/- answers),
  // re-synced whenever the saved answers change underneath (after Update / switching person).
  const saved: EraAnswer[] = Array.isArray(user_profile?.era_answers)
    ? user_profile.era_answers
    : [];
  const savedSig = JSON.stringify(saved);
  const [draft, setDraft] = useState<EraAnswer[]>(saved);
  const syncedSig = useRef(savedSig);
  useEffect(() => {
    if (syncedSig.current !== savedSig) {
      syncedSig.current = savedSig;
      setDraft(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSig]);

  const keyOf = (s: EraSeg) => `${s.start}|${s.end}`;
  const entriesFor = (s: EraSeg) =>
    draft
      .map((a, i) => ({ a, i }))
      .filter(({ a }) => a.start === s.start && a.end === s.end);
  const addTheme = (s: EraSeg, theme: string) => {
    if (!theme) return;
    setDraft((d) => [
      ...d,
      { start: s.start, end: s.end, theme, valence: "positive" },
    ]);
  };
  const toggleValence = (i: number) =>
    setDraft((d) =>
      d.map((a, j) =>
        j === i
          ? {
              ...a,
              valence: a.valence === "negative" ? "positive" : "negative",
            }
          : a,
      ),
    );
  const removeEntry = (i: number) =>
    setDraft((d) => d.filter((_, j) => j !== i));
  const dirty = JSON.stringify(draft) !== savedSig;
  const applyUpdate = () =>
    mutate((p: any) => {
      p.era_answers = draft;
      p.loop_count = 0;
    });

  if (err)
    return (
      <p className="muted">
        Life-periods need a birth date and place.
        {err !== "not_available" ? ` (${err})` : ""}{" "}
        {err !== "not_available" && (
          <button className="mono" onClick={() => setRetryNonce((n) => n + 1)}>
            ↻ Retry
          </button>
        )}
      </p>
    );
  if (!segments) return <p className="muted">Computing your life-periods…</p>;
  if (!segments.length)
    return (
      <p className="muted">
        No life-periods to show yet — add the birth date and place.
      </p>
    );

  return (
    <div>
      <p className="muted" style={{ marginTop: 0 }}>
        Each row is a stretch of your life ruled by one planet. The{" "}
        <b style={{ color: "var(--ink2)" }}>highlighted</b> rows are where your
        exact birth time is still uncertain. Add what each stretch was about —{" "}
        <b>several per period is better</b> — and tag each one{" "}
        <span style={{ color: "#7fd6a0" }}>good</span> or{" "}
        <span style={{ color: "#e79a9a" }}>hard</span>. A hard stretch points at
        a different sign than a good one, so both <b>narrow the rising sign</b>.
        Then press Update.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {segments.map((s) => {
          const isTx = s.kind === "transition";
          const entries = entriesFor(s);
          return (
            <div
              key={keyOf(s)}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                padding: "7px 10px",
                borderRadius: 8,
                border: `1px solid ${isTx ? "var(--warn-line, #6b5a1f)" : "var(--line)"}`,
                background: isTx
                  ? "var(--warn-bg, rgba(180,140,20,0.08))"
                  : "transparent",
              }}
            >
              <div
                style={{
                  width: 150,
                  fontFamily: "var(--mono)",
                  fontSize: 12,
                  color: "var(--ink3)",
                  paddingTop: 5,
                }}
              >
                {s.start} → {s.end}
              </div>
              <div style={{ flex: 1, fontSize: 13, paddingTop: 4 }}>
                {isTx ? (
                  <span>
                    ⚠️ uncertain —{" "}
                    <b>{(s.lords ?? []).map(_cap).join(" or ")}</b> period
                  </span>
                ) : (
                  <span>
                    <b>{_cap(s.lord)}</b> period
                  </span>
                )}
              </div>
              <div
                style={{
                  flex: "1.4",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6,
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                {entries.map(({ a, i }) => (
                  <span
                    key={i}
                    className={`era-chip ${a.valence === "negative" ? "neg" : "pos"}`}
                  >
                    <span>{_eraThemeLabel(a.theme)}</span>
                    <button
                      className="era-valence"
                      onClick={() => toggleValence(i)}
                      title="Toggle good / hard"
                    >
                      {a.valence === "negative" ? "hard" : "good"}
                    </button>
                    <button
                      className="era-x"
                      onClick={() => removeEntry(i)}
                      aria-label="Remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
                <select
                  className="influence-select"
                  value=""
                  aria-label="Add a theme for this life-period"
                  onChange={(e) => addTheme(s, e.target.value)}
                >
                  <option value="">
                    {entries.length
                      ? "+ add another…"
                      : isTx
                        ? "What happened? —"
                        : "+ add…"}
                  </option>
                  {ERA_THEME_OPTIONS.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          );
        })}
      </div>
      <div
        style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}
      >
        <button className="primary" disabled={!dirty} onClick={applyUpdate}>
          {dirty ? "↻ Update & recalculate" : "Up to date"}
        </button>
      </div>
    </div>
  );
}

// ── Saved information table (best-matched sign + per-item influence) ──────────────────────────

const INFLUENCE_OPTIONS: InfluenceLevel[] = [
  "high",
  "medium",
  "low",
  "minimal",
];
const _QBYKEY: Record<string, (typeof TRAIT_QUESTIONS)[number]> =
  Object.fromEntries(TRAIT_QUESTIONS.map((q) => [q.key, q]));

/** The sign a single trait OPTION most strongly indicates (argmax of its per-sign support map). */
function traitBestSign(key: string, opt: string): string | null {
  const option = _QBYKEY[key]?.options?.[opt];
  if (!option) return null;
  let bestIdx = -1;
  let bestV = 0;
  for (const [si, v] of Object.entries(option.signs)) {
    if (Number(v) > bestV) {
      bestV = Number(v);
      bestIdx = Number(si);
    }
  }
  return bestIdx >= 0 ? SIGN_NAMES[bestIdx] : null;
}

function SavedInfoTable({ signs }: { signs: SignRow[] | null }) {
  const { user_profile, mutate } = useProfile();
  const events: any[] = user_profile?.events ?? [];
  const traits: Record<string, any> = user_profile?.traits ?? {};
  const rejected: Record<string, any> = user_profile?.rejected_traits ?? {};
  const trait_influence: Record<
    string,
    Record<string, string>
  > = user_profile?.trait_influence ?? {};

  // Influence edits are STAGED here and only committed (re-scoring the heatmap) when the user
  // presses "Update" — so dragging several dropdowns doesn't fire a recompute on every change.
  const [pending, setPending] = useState<Record<string, InfluenceLevel>>({});
  const evKey = (i: number) => `ev:${i}`;
  const trKey = (key: string, opt: string) => `tr:${key}:${opt}`;
  const eventLevel = (i: number): InfluenceLevel =>
    pending[evKey(i)] ?? (events[i]?.influence as InfluenceLevel) ?? "high";
  const traitLevel = (key: string, opt: string): InfluenceLevel =>
    pending[trKey(key, opt)] ??
    (trait_influence[key]?.[opt] as InfluenceLevel) ??
    "high";
  const stage = (id: string, lvl: InfluenceLevel) =>
    setPending((prev) => ({ ...prev, [id]: lvl }));
  const hasPending = Object.keys(pending).length > 0;

  const applyUpdate = () => {
    mutate((p: any) => {
      for (const [id, lvl] of Object.entries(pending)) {
        if (id.startsWith("ev:")) {
          const idx = Number(id.slice(3));
          if (p.events?.[idx]) p.events[idx].influence = lvl;
        } else if (id.startsWith("tr:")) {
          const rest = id.slice(3);
          const sep = rest.lastIndexOf(":");
          const key = rest.slice(0, sep);
          const opt = rest.slice(sep + 1);
          const ti = (p.trait_influence ??= {});
          (ti[key] ??= {})[opt] = lvl;
        }
      }
      p.loop_count = 0; // evidence weighting changed → next scan re-seeds
    });
    setPending({});
  };

  // Deletes apply immediately (they're not influence edits); clear any staged influence so its
  // index-based keys can't go stale against the changed event list.
  const deleteEvent = (idx: number) => {
    setPending({});
    mutate((p: any) => {
      (p.events ?? []).splice(idx, 1);
      p.loop_count = 0;
    });
  };
  const deleteTrait = (
    field: "traits" | "rejected_traits",
    key: string,
    opt: string,
  ) => {
    setPending({});
    mutate((p: any) => {
      const arr = p[field]?.[key];
      if (Array.isArray(arr)) {
        p[field][key] = arr.filter((x: string) => x !== opt);
        if (!p[field][key].length) delete p[field][key];
      } else if (p[field]?.[key] === opt) {
        delete p[field][key];
      }
      p.loop_count = 0;
    });
  };

  // The rising sign + window a dated event best supports, from the breakdown's per-sign event scores.
  const eventBest = (ev: any): string => {
    if (!signs) return "—";
    const key = ev.label || ev.category || ev.start_date || "Event";
    let best: { sign: string; v: number; ws: string; we: string } | null = null;
    for (const s of signs) {
      const v = s.events?.[key];
      if (v == null) continue;
      if (!best || v > best.v)
        best = { sign: s.sign, v, ws: s.window_start, we: s.window_end };
    }
    return best
      ? `${best.sign} (${shortTime(best.ws)}–${shortTime(best.we)})`
      : "—";
  };

  const traitRows = Object.entries(traits).flatMap(
    ([key, opts]: [string, any]) =>
      (Array.isArray(opts) ? opts : [opts]).map((o: string) => ({ key, o })),
  );
  const rejectedRows = Object.entries(rejected).flatMap(
    ([key, opts]: [string, any]) =>
      (Array.isArray(opts) ? opts : [opts]).map((o: string) => ({ key, o })),
  );
  const isEmpty =
    events.length === 0 && traitRows.length === 0 && rejectedRows.length === 0;

  const InfluenceSelect = ({
    value,
    onChange,
  }: {
    value: InfluenceLevel;
    onChange: (l: InfluenceLevel) => void;
  }) => (
    <select
      className="influence-select"
      value={value}
      aria-label="Influence on the score"
      onChange={(e) => onChange(e.target.value as InfluenceLevel)}
    >
      {INFLUENCE_OPTIONS.map((l) => (
        <option
          key={l}
          value={l}
        >{`${titleCase(l)} · ${Math.round(INFLUENCE_FACTORS[l] * 100)}%`}</option>
      ))}
    </select>
  );

  return (
    <div style={{ overflowX: "auto" }}>
      <p className="caption" style={{ marginBottom: 10 }}>
        Everything on record. <strong>Influence</strong> sets how much each item
        counts toward its group's score (High 100% · Medium 50% · Low 25% ·
        Minimal 10%). Adjust as many as you like, then press
        <strong> Update</strong> to recalculate.
      </p>
      {isEmpty && (
        <p className="caption">
          Nothing saved yet — use “Add information” above.
        </p>
      )}
      {!isEmpty && (
        <>
          <table className="data saved-table" style={{ minWidth: 560 }}>
            <thead>
              <tr>
                <th>Item</th>
                <th>Group</th>
                <th>Best-matched sign</th>
                <th>Influence</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev, i) => (
                <tr key={`ev:${i}`}>
                  <td>
                    <strong>
                      {ev.label || titleCase(ev.category ?? "event")}
                    </strong>
                    <div className="caption">
                      {ev.start_date ?? "no date"}
                      {ev.event_time
                        ? ` · ${shortTime(ev.event_time)}`
                        : ""} · {titleCase(ev.category ?? "—")}
                    </div>
                  </td>
                  <td className="num" style={{ textAlign: "left" }}>
                    Events
                  </td>
                  <td
                    className="num"
                    style={{ textAlign: "left", color: "var(--ink2)" }}
                  >
                    {eventBest(ev)}
                  </td>
                  <td>
                    <InfluenceSelect
                      value={eventLevel(i)}
                      onChange={(l) => stage(evKey(i), l)}
                    />
                  </td>
                  <td>
                    <button
                      className="danger"
                      title="Delete"
                      onClick={() => deleteEvent(i)}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
              {traitRows.map(({ key, o }) => {
                const q = _QBYKEY[key];
                const group =
                  q?.target === "moon" ? "Personality" : "Appearance";
                const best = traitBestSign(key, o);
                return (
                  <tr key={`tr:${key}:${o}`}>
                    <td>{option_label(key, o)}</td>
                    <td className="num" style={{ textAlign: "left" }}>
                      {group}
                    </td>
                    <td
                      className="num"
                      style={{ textAlign: "left", color: "var(--ink2)" }}
                    >
                      {best
                        ? `${best}${group === "Personality" ? " (Moon)" : ""}`
                        : "—"}
                    </td>
                    <td>
                      <InfluenceSelect
                        value={traitLevel(key, o)}
                        onChange={(l) => stage(trKey(key, o), l)}
                      />
                    </td>
                    <td>
                      <button
                        className="danger"
                        title="Delete"
                        onClick={() => deleteTrait("traits", key, o)}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rejectedRows.map(({ key, o }) => (
                <tr key={`rj:${key}:${o}`}>
                  <td style={{ color: "var(--danger-ink)" }}>
                    Definitely not: {option_label(key, o)}
                  </td>
                  <td className="num" style={{ textAlign: "left" }}>
                    {_QBYKEY[key]?.target === "moon"
                      ? "Personality"
                      : "Appearance"}
                  </td>
                  <td
                    className="num"
                    style={{ textAlign: "left", color: "var(--ink3)" }}
                  >
                    absolute gate
                  </td>
                  <td className="caption">—</td>
                  <td>
                    <button
                      className="danger"
                      title="Delete"
                      onClick={() => deleteTrait("rejected_traits", key, o)}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 12,
            }}
          >
            <button
              className="primary"
              disabled={!hasPending}
              onClick={applyUpdate}
            >
              {hasPending ? "↻ Update & recalculate" : "Up to date"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Understanding the rising signs (purely informative) ──────────────────────────────────────

function SignsReference() {
  // Physical (rising-sign) signatures only — disposition is a Moon-sign read, shown in the Moon note,
  // and must not appear as a "rising sign" trait here (Vol_06 §2.2 vs §3.1).
  const signatures = all_sign_signatures(false);
  return (
    <div>
      <p className="caption" style={{ marginBottom: 12 }}>
        What each rising sign classically looks and carries itself like (Vol_06
        §2.2/§2.3/§2.5 — the SAME tables the scoring uses, so this reference can
        never drift from the ranking). Population-relative stereotypes that
        shortlist a sign; the dated events decide.
      </p>
      <div className="gloss-grid">
        {SIGN_NAMES.map((name) => {
          const sig: any = signatures[name] ?? {};
          const byQ: Record<string, string[]> = sig.by_question ?? {};
          return (
            <div key={name} className="gloss-card">
              <div>
                <span className="en">{name}</span>
                <span className="sa">{SANSKRIT[name]}</span>
              </div>
              {Object.entries(byQ).map(([short, labels]) => (
                <div key={short} className="caption" style={{ marginTop: 4 }}>
                  <span className="muted">{titleCase(short)}:</span>{" "}
                  {labels.join(" · ")}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Level 1 — exact-time heatmap ──────────────────────────────────────────────────────────────

interface ZoomFrame {
  start: string;
  end: string;
  /** Calendar dates of this frame, captured when it was pushed — so zooming back OUT across a
   *  midnight boundary restores the exact dated window (forward-only renormalisation cannot step
   *  a day backwards). Optional: stacks persisted before this field fall back to renormalising. */
  start_date?: string | null;
  end_date?: string | null;
}

/** Compact line for a candidate's secondary Vedic checks (PLAN §6 — corroborate, never decide). */
export function SpecialPointsLine({ sp }: { sp: any }) {
  if (!sp) return null;
  return (
    <div className="caption">
      Pranapada house {sp.pranapada_house}
      {sp.pranapada_auspicious ? " ✓ auspicious" : " (neutral)"} · Ghati{" "}
      {sp.ghati_lagna_sign} · Hora {sp.hora_lagna_sign}
      {sp.varnad_lagna ? ` · Varnad ${sp.varnad_lagna}` : ""}
      {sp.upagrahas?.gulik
        ? ` · Gulika ${sp.upagrahas.gulik.sign} (house ${sp.upagrahas.gulik.house_from_lagna})`
        : ""}
      {sp.gulika_in_lagna ? " · ⚠ Gulika in the Lagna" : ""}
      {sp.gandanta?.lagna_gandanta ? " · ⚠ Lagna Gandanta" : ""}
      {sp.gandanta?.moon_gandanta
        ? " · ⚠ Moon Gandanta (dasha balance flips)"
        : ""}
    </div>
  );
}

/**
 * The clicked-candidate detail panel for the exact-time strip — the Level-1 twin of `HoverPanel`.
 * A click OPENS it (the band no longer drills directly); it reads out the time's Events/Accuracy
 * fits, which event holds it up, and its secondary checks, then offers "Zoom into this range" (the
 * drill-down) or Close. `canZoom` is false at the finest window (≤2 min), where there is nothing
 * left to drill into.
 */
function CandidateDetailPanel({
  item,
  eventCount,
  canZoom,
  pending,
  onZoom,
  onClose,
  marked,
  markFull,
  onMark,
}: {
  item: any | null;
  eventCount: number;
  canZoom: boolean;
  pending?: boolean;
  onZoom: () => void;
  onClose: () => void;
  /** Is this exact second already on the comparison shortlist? */
  marked?: boolean;
  /** Shortlist full (MAX_MARKED_TIMES) — disables marking with an honest tooltip. */
  markFull?: boolean;
  /** Add this time (seconds included) to the comparison shortlist. */
  onMark?: () => void;
}) {
  if (!item)
    return (
      <div className="heatmap-hover muted">
        Hover a band to see its time; <strong>click</strong> it to open the
        details and zoom into that range.
      </div>
    );
  const det = item.deterministic_score ?? item.event_fit_score;
  const ul: CSSProperties = {
    margin: "5px 0 0",
    paddingLeft: 16,
    listStyleType: "disc",
  };
  const li: CSSProperties = { marginBottom: 3, lineHeight: 1.45 };
  const keyStyle: CSSProperties = { color: "var(--ink3)" };
  return (
    <div className="heatmap-hover">
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <strong style={{ fontFamily: "var(--mono)", fontSize: 15 }}>
          {item.time}
        </strong>
        <span className="band">{bandLabel(det)}</span>
        <span className="muted">
          {audit_candidate(item, eventCount).lead ?? ""}
        </span>
      </div>
      <ul style={ul}>
        <li style={li}>
          <span style={keyStyle}>Events fit:</span>{" "}
          <strong>{fmtPct(item.event_fit_score)}</strong>
        </li>
        <li style={li}>
          <span style={keyStyle}>Explains:</span>{" "}
          <strong>
            {item.explained_fit != null
              ? `${item.explained_count ?? "?"}/${item.events_total ?? "?"} events (${Number(item.explained_fit).toFixed(1)}%)`
              : "—"}
          </strong>
        </li>
        <li style={li}>
          <span style={keyStyle}>Accuracy (held-out):</span>{" "}
          <strong>{fmtPct(item.accuracy_fit)}</strong>
        </li>
      </ul>
      <div className="caption" style={{ marginTop: 4 }}>
        {audit_sentence(item, eventCount)}
      </div>
      <SpecialPointsLine sp={item.special_points} />
      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 10,
          borderTop: "1px solid var(--line)",
          paddingTop: 9,
        }}
      >
        {canZoom ? (
          <button
            type="button"
            onClick={onZoom}
            disabled={pending}
            style={{
              background: "var(--accent)",
              color: "#15151b",
              border: "none",
              borderRadius: 8,
              padding: "7px 14px",
              fontWeight: 600,
              fontSize: 13,
              cursor: pending ? "default" : "pointer",
              opacity: pending ? 0.6 : 1,
            }}
          >
            🔍 Zoom into this range
          </button>
        ) : (
          <span className="muted" style={{ fontSize: 12.5 }}>
            Finest resolution — the precision gate governs the claim.
          </span>
        )}
        {onMark && (
          <button
            type="button"
            onClick={onMark}
            disabled={marked || markFull}
            title={
              marked
                ? "Already on the comparison shortlist"
                : markFull
                  ? `The shortlist is full (${MAX_MARKED_TIMES}) — remove a time on the comparison page first.`
                  : "Save this exact time (to the second) to the side-by-side comparison shortlist"
            }
            style={{
              background: "transparent",
              color: marked ? "var(--g2)" : "var(--accent)",
              border: `1px solid ${marked ? "var(--g2)" : "var(--accent)"}`,
              borderRadius: 8,
              padding: "7px 12px",
              fontSize: 13,
              cursor: marked || markFull ? "default" : "pointer",
              opacity: markFull && !marked ? 0.6 : 1,
            }}
          >
            {marked ? "★ Marked for comparison ✓" : "★ Add time to compare"}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "transparent",
            color: "var(--ink2)",
            border: "1px solid var(--line)",
            borderRadius: 8,
            padding: "7px 12px",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}

function ExactTimeHeatmap({
  moonNote,
  belowBars,
  onOpenCompare,
}: {
  moonNote?: ReactNode;
  belowBars?: ReactNode;
  onOpenCompare?: () => void;
}) {
  const { user_profile, runOp, mutate, reload } = useProfile();
  // The candidate whose detail panel is OPEN — set by CLICKING a band. The panel offers Close or
  // "Zoom into this range" (the drill-down); clicking a band no longer drills directly.
  const [selected, setSelected] = useState<{ item: any; rank: number } | null>(
    null,
  );
  // The drill-down zoom history is PERSISTED on the profile (`zoom_stack`) so reopening the profile
  // restores the exact depth and zoom-out trail the user left on. The current window is `search`.
  const stack: ZoomFrame[] = user_profile?.zoom_stack ?? [];
  const stackLen = stack.length;
  const [metric, setMetric] = useState<DrillMetricKey>("events");
  // The REAL re-rectification verdict for the committed sign window: leave-one-out re-searches the
  // window without each event and reports how well the held-out events still fit + how stable the
  // time is. Fetched at the committed window (not while drilled deeper, where a 2-min sub-window
  // would read trivially stable); kept on screen as context while the user drills in.
  const [holdout, setHoldout] = useState<{
    verdict: string;
    fit: number | null;
  } | null>(null);
  const holdoutLoaded = useRef<string | null>(null);
  const zoomAction = useAsyncAction();
  const holdoutSig = JSON.stringify({
    s: user_profile?.search?.start,
    e: user_profile?.search?.end,
    ev: user_profile?.events,
    locked: user_profile?.rising_sign_locked,
  });
  useEffect(() => {
    // Only at the committed sign window (no drill frames pushed) and only once a sign is locked.
    if (!user_profile?.rising_sign_locked || stackLen > 0) return;
    if (holdoutLoaded.current === holdoutSig) return;
    holdoutLoaded.current = holdoutSig;
    let alive = true;
    let landed = false;
    (async () => {
      const res = await runOp("holdout_validation", {});
      if (!alive) return;
      landed = true;
      const v: any = res.result;
      setHoldout(
        v?.available
          ? { verdict: v.verdict, fit: v.mean_held_out_fit ?? null }
          : null,
      );
    })().catch(() => {
      if (!alive) return;
      landed = true;
      setHoldout(null);
      holdoutLoaded.current = null;
    });
    return () => {
      alive = false;
      // The in-flight result was dropped (drilled in mid-fetch) — clear the stamp so returning
      // to this same window actually refetches instead of skipping forever.
      if (!landed) holdoutLoaded.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdoutSig, stackLen]);
  // The selected candidate belongs to the CURRENT window; drop it whenever the window changes
  // (drill in/out), so the panel never shows a candidate that the re-scan has replaced.
  useEffect(() => {
    setSelected(null);
  }, [user_profile?.search?.start, user_profile?.search?.end]);
  if (!user_profile) return null;

  const search = user_profile.search ?? {};
  const gate = precision_tier(user_profile);
  const candidates = candidate_list_from_times(user_profile); // sorted by score
  const eventCount = (user_profile.events ?? []).length;
  const cluster = candidate_cluster(candidates);

  // ── Marked-times shortlist ("Add time to compare") ──
  // The key resolves the candidate's calendar day the same way the engine does (overnight/
  // multi-day safe), so 00:30 in a 21:00→01:00 window is marked on the day AFTER the birth date.
  const markedEntries = normalize_marked_times(user_profile);
  const markedKeys = new Set(markedEntries.map(marked_time_key));
  const markKeyOf = (item: any): string | null => {
    if (!item?.time) return null;
    const t = String(item.time);
    const clock = t.includes("T") ? t.split("T").pop()! : t;
    return `${resolve_marked_date(user_profile!, t)}T${clock}`;
  };
  const markCandidate = (item: any) => {
    if (!item?.time) return;
    // Marking is a shortlist edit, NOT evidence — it must never reset loop_count or re-scan.
    mutate((p: any) => {
      add_marked_time(p, String(item.time), item.ascendant_sign ?? null, new Date().toISOString());
    });
  };

  const bandStr = cluster
    ? `${shortTime(cluster.low_time)}–${shortTime(cluster.high_time)}`
    : `${shortTime(search.start ?? "?")}–${shortTime(search.end ?? "?")}`;
  const estimate =
    gate.tier === "second"
      ? (cluster?.top_time ?? bandStr)
      : gate.tier === "minute"
        ? cluster
          ? `≈ ${shortTime(cluster.top_time)}`
          : bandStr
        : bandStr;

  const winStart = secondsOfLabel(search.start ?? "00:00:00");
  const winWidth = Math.max(
    forwardWidthSeconds(search.start ?? "00:00:00", search.end ?? "23:59:59"),
    1,
  );
  const byTime = [...candidates]
    .filter((c: any) => c.time)
    .map((c: any) => {
      let pos = secondsOfLabel(c.time) - winStart;
      if (pos < 0) pos += 86400;
      return { c, pos: Math.max(0, Math.min(pos, winWidth)) };
    })
    .sort((a, b) => a.pos - b.pos);
  const rankByTime = new Map<any, number>();
  candidates.forEach((c: any, i: number) => rankByTime.set(c, i + 1));

  // Move to `next` and persist `nextStack` as the new zoom history, then re-scan the window. The
  // stack lives on the profile so it survives a reload (mutate persists it; the worker round-trip
  // preserves it, exactly like the `search` window it travels with).
  async function applyWindow(next: ZoomFrame, nextStack: ZoomFrame[]) {
    await zoomAction.run("Re-scanning the zoomed window…", async () => {
      mutate((p: any) => {
        const sr = (p.search ??= {});
        // Captured before the overwrite: the new window's dates resolve against the one it was
        // carved from (see renormalizeWindowDates — fixes the stale-date staleness at drill depth).
        const base = {
          multiday: searchIsMultiday(sr),
          start: (sr.start ?? null) as string | null,
          start_date: (sr.start_date ?? p.birth?.date ?? null) as string | null,
        };
        sr.start = next.start;
        sr.end = next.end;
        if (next.start_date && next.end_date) {
          // Zoom-OUT restoration: the frame recorded its own dates — put them back verbatim
          // (renormalisation is forward-only and cannot step back across midnight).
          sr.start_date = next.start_date;
          sr.end_date = next.end_date;
        } else {
          renormalizeWindowDates(sr, base);
        }
        p.zoom_stack = nextStack;
        p.loop_count = 0;
      });
      await runOp("run_internal_matching");
      reload();
    });
  }

  function zoomInto(centerLabel: string) {
    const curWidth = winWidth;
    if (curWidth <= 120) return; // already ~2 min — fine enough; the gate governs the claim
    const newWidth = Math.max(Math.floor(curWidth / 4), 120);
    // FORWARD-from-window-start coordinates, like every other position on the strip: a raw
    // seconds-of-day centre would put a post-midnight candidate (00:30 in a 23:00→01:00 window)
    // BEFORE the window start and always snap the zoom to the start instead of around it.
    const center =
      winStart + ((((secondsOfLabel(centerLabel) - winStart) % 86400) + 86400) % 86400);
    let s = center - Math.floor(newWidth / 2);
    let e = s + newWidth;
    const lo = winStart;
    const hi = winStart + curWidth;
    if (s < lo) {
      s = lo;
      e = s + newWidth;
    }
    if (e > hi) {
      e = hi;
      s = e - newWidth;
    }
    // Push the CURRENT window onto the stack so zoom-out (and a later reload) can step back to it.
    // The frame carries its dates so stepping back OUT across midnight restores them exactly.
    const current: ZoomFrame[] =
      search.start && search.end
        ? [
            {
              start: search.start,
              end: search.end,
              start_date: search.start_date ?? null,
              end_date: search.end_date ?? null,
            },
          ]
        : [];
    void applyWindow({ start: labelOfSeconds(s), end: labelOfSeconds(e) }, [
      ...stack,
      ...current,
    ]);
  }

  function zoomOut() {
    if (stack.length > 0) {
      const prev = stack[stack.length - 1];
      void applyWindow(prev, stack.slice(0, -1));
      return;
    }
    // Empty stack = step back OUT of the commit, to the BASE search window: the window the sign
    // was committed from (base_window), else the birth range the intake or an "Edit window"
    // correction recorded. NEVER a hardcoded full day — that used to blow a narrower base (an
    // intake ± window, or a corrected 14:00→02:00 overnight) back up to 00:00–23:59 and PERSIST
    // it, destroying the user's window on the first zoom-out past the commit level.
    void zoomAction.run("Widening back to the full search window…", async () => {
      mutate((p: any) => {
        const target = zoomOutTarget(p);
        const sr = (p.search ??= {});
        sr.start = target.start;
        sr.end = target.end;
        // Both dates from the base: a drill that had landed past midnight (start_date = birth
        // date + 1) must not leave start_date AFTER end_date on the restored window.
        sr.start_date = target.start_date ?? p.birth?.date ?? sr.start_date;
        sr.end_date = target.end_date ?? p.birth?.date ?? sr.end_date;
        p.loop_count = 0;
        p.times = {};
        p._times = {
          schema_version: 1,
          user_slug: p.slug ?? "user",
          times: {},
        };
        // Back to picking a sign — release the commit and clear the drill history so the base
        // window shows the Level-0 rising-sign view from scratch.
        p.rising_sign_locked = false;
        p.zoom_stack = [];
      });
      reload();
    });
  }

  const drilled = stack.length > 0;
  // Once ANY candidate lands off a whole minute (deep drill-down, 15s/1s steps), HH:MM would
  // collapse neighbours seconds apart into identical labels — switch the strip to full seconds.
  const secondsMatter = byTime.some(({ c }) => !String(c.time ?? "").endsWith(":00"));
  const clockLabel = secondsMatter ? fullClock : shortTime;
  const bars: Bar[] = byTime.map(({ c, pos }, i) => {
    const next = byTime[i + 1];
    // Each band spans from its own start to the next candidate's start; the first absorbs the
    // lead-in from the window start. Placed by fraction of the window so it tracks the axis.
    const bandStart = i === 0 ? 0 : pos;
    const bandEnd = next ? next.pos : winWidth;
    const det =
      c.deterministic_score ?? c.event_fit_score ?? c.calculated_score;
    const rank = rankByTime.get(c) ?? i + 1;
    return {
      key: c.time ?? String(i),
      leftFrac: bandStart / winWidth,
      widthFrac: Math.max((bandEnd - bandStart) / winWidth, 0.002),
      score: det != null ? Number(det) : null,
      name: clockLabel(c.time),
      sub: "→ " + clockLabel(c.time),
      pct: det != null ? `${Number(det).toFixed(1)}%` : "—",
      // Only Events, Explains and Accuracy vary per second within a committed sign.
      metrics: {
        events: c.event_fit_score ?? (det != null ? Number(det) : null),
        explains: c.explained_fit ?? null,
        accuracy: c.accuracy_fit ?? null,
        eras: null,
        appearance: null,
        personality: null,
        derived: null,
      },
      mono: true,
      // Click OPENS the detail panel (it no longer drills directly); the panel's button drills —
      // mirroring the Level-0 "click a band → panel → zoom" flow, at every drill depth.
      cursor: "pointer",
      // Hover shows a lightweight floating time pill; the rich read-out moved to the click panel.
      hint: clockLabel(c.time),
      disabled: zoomAction.pending,
      onClick: () => setSelected({ item: c, rank }),
    };
  });

  // Accuracy needs ≥2 dated events scoring at a time (else there's nothing to hold out); Explains
  // is absent on records scanned before 2026-07-20. When no candidate carries the selected metric,
  // disable that toggle and fall back to Events so the strip is never blank.
  const hasAccuracy = bars.some((b) => b.metrics?.accuracy != null);
  const hasExplains = bars.some((b) => b.metrics?.explains != null);
  const shownMetric: DrillMetricKey =
    (metric === "accuracy" && !hasAccuracy) || (metric === "explains" && !hasExplains)
      ? "events"
      : metric;

  return (
    <>
      <div className="heatmap-sticky">
        <div className="heatmap-panel">
          <div className="heatmap-topbar">
            <div className="crumbs">
              {(() => {
                // Honest crumb: zoom-out lands on the BASE search window, which is the full day
                // only when the intake/corrected range actually spans it.
                const t = zoomOutTarget(user_profile);
                const label =
                  t.start === "00:00:00" && t.end === "23:59:59"
                    ? "Full day"
                    : `${shortTime(t.start)}–${shortTime(t.end)}`;
                return (
                  <button onClick={zoomOut} disabled={zoomAction.pending}>
                    {label}
                  </button>
                );
              })()}
              <span className="sep">›</span>
              <button className="here">
                {shortTime(search.start ?? "?")}–{shortTime(search.end ?? "?")}
                {drilled ? " · drill" : ""}
              </button>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <Legend rank />
              <WindowEditor
                pending={zoomAction.pending}
                onApply={(startT, endT, endDate) =>
                  zoomAction.run("Rescanning the corrected window…", async () => {
                    // Editing the window from Level 1 clears the sign lock inside
                    // replace_search_window, so the page returns to Level 0 over the new window.
                    // mutate saves locally AND pushes to DynamoDB — the correction is permanent.
                    mutate((p: any, r: any) =>
                      replace_search_window(p, startT, endT, parseISODate(p.birth.date), endDate, r),
                    );
                    await runOp("run_internal_matching");
                    reload();
                  })
                }
              />
              <CompareTimesButton onOpen={onOpenCompare} />
              <button
                className="mono zoom-out-btn"
                onClick={zoomOut}
                disabled={zoomAction.pending}
              >
                ⤺ Zoom out
              </button>
            </div>
          </div>

          <div className="level-caption">
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <h2>
                {drilled
                  ? "Drill-down — the exact moment"
                  : "Cycle 2 — timing the chosen window"}
              </h2>
              <span className="sub">
                {drilled
                  ? "every second across the chosen span"
                  : "piecewise band over cusp / D60 boundaries"}
              </span>
            </div>
            <span className="res-note">
              {drilled ? "step = 1s" : "NARROW · 15s scan"}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 8,
            }}
          >
            <div
              className="metric-toggle"
              role="group"
              aria-label="Choose which metric the bars show"
            >
              {DRILL_METRIC_OPTIONS.map((m) => {
                const disabled =
                  (m.key === "accuracy" && !hasAccuracy) ||
                  (m.key === "explains" && !hasExplains);
                return (
                  <button
                    key={m.key}
                    type="button"
                    className={`metric-toggle-btn${shownMetric === m.key ? " active" : ""}`}
                    aria-pressed={shownMetric === m.key}
                    disabled={disabled}
                    title={
                      disabled
                        ? "Needs at least 2 dated events to hold out — add more dated events."
                        : m.help
                    }
                    onClick={() => setMetric(m.key)}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
            {holdout && (
              <span
                className="top-fit-chip"
                title="Leave-one-out re-rectification: the window is re-searched without each event in turn; this is how well the held-out events still fit and how stable the time is."
              >
                <span className="top-fit-k">Held-out accuracy</span>
                <span
                  className="top-fit-v"
                  style={{
                    color:
                      {
                        high: "var(--g2)",
                        medium: "var(--accent2)",
                        low: "var(--b2)",
                      }[holdout.verdict] ?? "var(--ink3)",
                  }}
                >
                  {holdout.verdict}
                  {holdout.fit != null ? ` · ${holdout.fit.toFixed(1)}%` : ""}
                </span>
              </span>
            )}
          </div>

          {bars.length === 0 ? (
            <div
              className="muted"
              style={{ padding: "2.5rem 1rem", textAlign: "center" }}
            >
              No candidate times yet — timing the window…
              {/* A failed scan (e.g. a transient worker error) must be recoverable in place —
                  without this the drill view would sit on the placeholder forever. */}
              <div style={{ marginTop: 12 }}>
                <button
                  className="mono"
                  disabled={zoomAction.pending}
                  onClick={() =>
                    void zoomAction.run("Re-scanning the window…", async () => {
                      await runOp("run_internal_matching");
                      reload();
                    })
                  }
                >
                  ↻ Re-scan this window
                </button>
              </div>
            </div>
          ) : (
            <>
              <HeatmapBars
                bars={bars}
                label={`Exact-time heatmap — ${shownMetric === "events" ? "Events" : shownMetric === "explains" ? "Explains" : "Accuracy"}`}
                metric={shownMetric}
                rankColors
              />
              <AxisTicks
                start={search.start ?? "00:00:00"}
                end={search.end ?? "23:59:59"}
              />
            </>
          )}

          <PrecisionHero
            tier={gate.tier}
            readout={estimate}
            range={
              cluster
                ? `± cluster ${cluster.spread_minutes} min`
                : "candidate window"
            }
            message={gate.reason || gate.headline}
            resolutionLabel={gate.tier}
          />
        </div>

        <CandidateDetailPanel
          item={selected?.item ?? null}
          eventCount={eventCount}
          canZoom={winWidth > 120}
          pending={zoomAction.pending}
          onClose={() => setSelected(null)}
          onZoom={() => {
            const t = selected?.item?.time;
            setSelected(null);
            if (t) zoomInto(t);
          }}
          marked={(() => {
            const k = selected ? markKeyOf(selected.item) : null;
            return k ? markedKeys.has(k) : false;
          })()}
          markFull={markedEntries.length >= MAX_MARKED_TIMES}
          onMark={selected ? () => markCandidate(selected.item) : undefined}
        />
      </div>

      {zoomAction.pending && <LoadingLine message={zoomAction.message} />}
      {zoomAction.error && <Banner kind="error">{zoomAction.error}</Banner>}

      {belowBars}

      {moonNote}

      {gate.missing && <Banner kind="warn">{gate.missing}</Banner>}

      {!gate.allow_exact_second && (
        <Expander
          summary={
            <PanelSummary
              no="✦"
              title="Want a second-level time?"
              meta="add a document-backed timed anchor"
            />
          }
        >
          <p className="caption">
            Two clock-timed, located events unlock a second-level claim. Good
            anchors (leave unknown times blank — never guess):
          </p>
          <ul className="caption">
            {ANCHOR_EVENT_SUGGESTIONS.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </Expander>
      )}

      <Expander
        summary={
          <PanelSummary
            no="🔬"
            title="Inspect candidates"
            meta="scores & supporting events"
          />
        }
      >
        {candidates.slice(0, 12).map((item: any, i: number) => {
          const det = item.deterministic_score ?? item.event_fit_score;
          const details: any[] = item.kp_details ?? item.event_details ?? [];
          return (
            <div key={item.time ?? i} className="candidate-inspect">
              <div
                className="row"
                style={{ alignItems: "center", gap: "0.5rem" }}
              >
                <span className="tree-score">{fmtScore(det)}</span>
                <strong style={{ fontFamily: "var(--mono)" }}>
                  {item.time}
                </strong>
                <span className="muted" style={{ marginLeft: "auto" }}>
                  {audit_candidate(item, eventCount).lead ?? ""}
                </span>
              </div>
              <p className="caption">{audit_sentence(item, eventCount)}</p>
              <SpecialPointsLine sp={item.special_points} />
              {item.appearance_note && (
                <p className="caption" style={{ color: "var(--accent2)" }}>
                  ⚖︎ {item.appearance_note}
                </p>
              )}
              {details.length > 0 && (
                <table className="data">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th style={{ textAlign: "right" }}>Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.map((d, j) => (
                      <tr key={j}>
                        <td>{d.label || d.category || `Event ${j + 1}`}</td>
                        <td className="num">{fmtScore(d.score)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </Expander>
    </>
  );
}
